import { useEffect, useState, useRef, useCallback, useMemo, Fragment } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { FileText, Save, Edit, User, Camera, ArrowLeft, PlayCircle, PauseCircle, Mic, PhoneCall, CalendarClock, Phone, AlertTriangle, CheckCircle, XCircle, Clock, Cake, User2, ShieldCheck, Mail, Calendar, CreditCard, Copy, Loader2, X, RefreshCw, Video, Building2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { formatDate, formatPhoneDisplay } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "sonner";
import { z } from "zod";
import useAuth from "@/hooks/useAuth";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { getCurrentUser } from "aws-amplify/auth";
import AuthService from "@/lib/auth";
import EmailService from "@/lib/email";
import { buildRegistrationEmailHtml } from "@/utils/registrationEmail";
import biometricAuthService from "@/services/biometricAuthService";
import CallService from "@/services/CallService";
import MindWiseService, { type MindWiseSyncResponse, type MindWiseTestScenario } from "@/services/MindWiseService";
import { MindWiseSyncDialog } from "@/components/mindwise/MindWiseSyncDialog";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import SimpleRichTextEditor from "@/components/ui/customRichTextEditor";
import MainLayout from "@/layouts/MainLayout.tsx";

// Consent log interface
interface ConsentLog {
  type: "hipaa_consent" | "email_consent" | "sms_consent" | string;
  uuid: string;
  status: "accepted" | "rejected" | "withdrawn";
  details: string;
  timestamp: string;
  recorded_at?: string;
}


// Enhanced role formatting function
const formatRoleName = (role: string) => {
  if (!role) return "Unknown";

  const roleMap: { [key: string]: string } = {
    doctor: "Doctor",
    nurse: "Nurse",
    admin: "Administrator",
    staff: "Staff Member",
    receptionist: "Receptionist",
    manager: "Manager",
    clinical_staff: "Clinical Staff",
    clinical: "Clinical Staff",
    clinical_reviewer: "Clinical Reviewer",
    email_manager: "Email Manager",
    sms_manager: "SMS Manager",
    patient: "Patient",
  };

  const lowerRole = role.toLowerCase().replace(/ /g, "_");
  return (
    roleMap[lowerRole] ||
    role
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  );
};


interface QuestionnaireResponse {
  id: number;
  patient_id: string;
  questionnaire_type: string;
  answers: string;
  total_score: number;
  notes: string | null;
  recorded_by: string | null;
  self_reported: boolean;
  created_at: string;
  token: string;
  expires_at: string;
  sms_sent_at: string | null;
  status: string;
  updated_at: string | null;
  review_notes?: string | null;
  reviewed_by?: string | null;
  reviewed_by_name?: string | null;
  reviewed_at?: string | null;
  time_spent_on_review?: number | null;
  voice_url?: string | null;
}

interface PatientEncounter {
  patient_id: string;
  encounter_date: string;
  encounter_type: string;
  status: string;
  primary_provider_id: string | null;
  secondary_provider_id: string | null;
  facility_id: string | null;
  department: string | null;
  chief_complaint: string | null;
  diagnosis_primary: string | null;
  diagnosis_secondary: string | null;
  start_time: string | null;
  end_time: string | null;
  visit_reason: string | null;
  visit_priority: string | null;
  clinical_notes: string | null;
  encounter_summary: string | null;
  follow_up_instructions: string | null;
  created_at: string;
  updated_at: string | null;
  sms_sent_at: string | null;
  sms_expires_at: string | null;
  encounter_review_status: string | null;
  review_recorded_by: string | null;
  encounter_start_time?: string | null;
  clinical_review_started_at?: string | null;
  clinical_review_ended_at?: string | null;
  clinical_review_duration?: number | null;  // seconds in postgres
  location_ids: number[];
  encounter_id: string;
  branch_name: string | null;
  branch_id: string | null;
}

interface SmsLog {
  id: string;
  created_at?: string;
  updated_at?: string;
  sent_times?: string | number;
  patient_id: string;
  creator_id: string | null;
  unique_page_id: string;
  message_body: string;
  status: string;
}

interface ChartDataPoint {
  date: string;
  [key: string]: any;
}

interface PatientData {
  id: string;
  first_name: string;
  middle_name?: string | null;
  last_name: string;
  selected_tests?: string[];
  date_of_birth: string;
  mobile_number: string;
  photo_url: string | null;
  email: string | null;
  gender: string;
  receive_sms: boolean;
  general_notes?: string;
  created_at: string;
  auth_uuid: string;
  consent_logs: ConsentLog[];
  location_ids: number[];
  enrolled_for_monitorhealth_services?: boolean;
  [key: string]: any;
}

const calculateMaxScore = (questions: any[]): number | null => {
  if (!Array.isArray(questions)) return null;
  let maxScore = 0;
  let hasValidOptions = false;

  for (const q of questions) {
    const options = q.options || q.option;
    if (Array.isArray(options) && options.length > 0) {
      hasValidOptions = true;
      let maxForQ = 0;
      for (const opt of options) {
        const val = Number(opt.value);
        if (!isNaN(val) && val > maxForQ) maxForQ = val;
      }
      maxScore += maxForQ;
    }
  }

  return hasValidOptions ? maxScore : null;
};

function PatientDetailsContent() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const auth = useAuth();
  const [patient, setPatient] = useState<PatientData | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [, setAvailableTests] = useState<string[]>([]);
  const [questionnaireTemplates, setQuestionnaireTemplates] = useState<
    { id: string; name: string; age_min?: number; age_max?: number; source_citations?: string | null }[]
  >([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);

  const formSchema = z.object({
    first_name: z.string().min(1, "First name is required"),
    middle_name: z.string().optional(),
    last_name: z.string().min(1, "Last name is required"),
    social_security_number: z.string()
      .optional()
      .refine((val) => !val || /^\d{3}-\d{2}-\d{4}$/.test(val), "SSN must be in format 123-45-6789"),
    photo: z.any().optional(),
    selected_tests: z.array(z.string()).optional(),
    primary_provider_id: z.string().optional(),
    secondary_provider_id: z.string().optional(),
    date_of_birth: z.string().min(1, "Date of birth is required"),
    email: z.string().email("Invalid email").or(z.literal("")),
    mobile_number: z
      .string()
      .min(10, "Phone number must be at least 10 digits"),
  });

  type FormValues = z.infer<typeof formSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema) as any,
    defaultValues: {
      first_name: "",
      last_name: "",
      selected_tests: [],
      date_of_birth: "",
      email: "",
      mobile_number: "",
      middle_name: "",
      social_security_number: "",
    },
  });

  // CMS Alert History state
  const [cmsAlerts, setCmsAlerts] = useState<any[]>([]);
  const [cmsAlertsLoading, setCmsAlertsLoading] = useState(false);
  const [cmsAlertActionLoading, setCmsAlertActionLoading] = useState<number | null>(null);
  const [cmsResolveModalId, setCmsResolveModalId] = useState<number | null>(null);
  const [cmsResolveNotes, setCmsResolveNotes] = useState("");

  const [questionnaireResponses, setQuestionnaireResponses] = useState<
    QuestionnaireResponse[]
  >([]);
  const [patientEncounters, setPatientEncounters] = useState<
    PatientEncounter[]
  >([]);
  const [smsLogs, setSmsLogs] = useState<SmsLog[]>([]);
  const [staffUsers, setStaffUsers] = useState<any[]>([]);
  const [branchLocations, setBranchLocations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [selectedTests, setSelectedTests] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<string>("all");

  // Face registration modal state
  const [showFaceRegistrationModal, setShowFaceRegistrationModal] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordingPhase, setRecordingPhase] = useState<"initial" | "additional">("initial");
  const [statusMessage, setStatusMessage] = useState<string>("");
  const [recordingDisabled, setRecordingDisabled] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [recordedVideos, setRecordedVideos] = useState<File[]>([]);
  const [showFaceFoundDialog, setShowFaceFoundDialog] = useState(false);
  const [existingFaceData, setExistingFaceData] = useState<any>(null);
  const recordingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isDialogOpenRef = useRef(false);
  const [testNames, setTestNames] = useState<string[]>([]);
  const [editingResponse, setEditingResponse] =
    useState<QuestionnaireResponse | null>(null);
  const [reviewNote, setReviewNote] = useState("");
  const [showAuditReport, setShowAuditReport] = useState(false);
  const [auditReportLoading, setAuditReportLoading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [isEmailReportModalOpen, setIsEmailReportModalOpen] = useState(false);
  const [emailModalPrimaryName, setEmailModalPrimaryName] = useState("");
  const [emailModalPrimaryEmail, setEmailModalPrimaryEmail] = useState("");
  const [emailModalPrimarySelected, setEmailModalPrimarySelected] = useState(true);
  const [emailModalHasSecondary, setEmailModalHasSecondary] = useState(false);
  const [emailModalSecondaryName, setEmailModalSecondaryName] = useState("");
  const [emailModalSecondaryEmail, setEmailModalSecondaryEmail] = useState("");
  const [emailModalSecondarySelected, setEmailModalSecondarySelected] = useState(false);
  const [emailModalCustomSelected, setEmailModalCustomSelected] = useState(false);
  const [emailModalCustomName, setEmailModalCustomName] = useState("");
  const [emailModalCustomEmail, setEmailModalCustomEmail] = useState("");

  const [viewingReviewNote, setViewingReviewNote] = useState<{
    response: QuestionnaireResponse;
  } | null>(null);

  const [isScheduleCallModalOpen, setIsScheduleCallModalOpen] = useState(false);
  const [callNowLoading, setCallNowLoading] = useState(false);
  const [currentCallLog, setCurrentCallLog] = useState<any>(null);
  const [allCallLogs, setAllCallLogs] = useState<any[]>([]);
  const [fetchingCallLog, setFetchingCallLog] = useState(false);
  const [scheduleCallDate, setScheduleCallDate] = useState<string>("");
  const [scheduleCallTime, setScheduleCallTime] = useState<string>("");
  const [testAttemptDialog, setTestAttemptDialog] = useState<{ isOpen: boolean, testId: string | null }>({ isOpen: false, testId: null });
  const [mindwiseSyncing, setMindwiseSyncing] = useState(false);
  const [mindwiseSyncResult, setMindwiseSyncResult] = useState<MindWiseSyncResponse | null>(null);
  const [isMindWiseModalOpen, setIsMindWiseModalOpen] = useState(false);

  const handleMindWiseSync = async (scenario?: MindWiseTestScenario) => {
    if (!id) return;
    if (scenario) {
      MindWiseService.setTestScenario(scenario);
    }
    setMindwiseSyncing(true);
    try {
      const res = await MindWiseService.syncPatientAssessments(id);
      setMindwiseSyncResult(res);
      setIsMindWiseModalOpen(true);
    } catch (e) {
      // error toast handled in MindWiseService
    } finally {
      setMindwiseSyncing(false);
    }
  };

  const auditEditorRef = useRef<HTMLDivElement>(null);

  const [currentUser, setCurrentUser] = useState<any>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [isDoctor, setIsDoctor] = useState<boolean>(false);
  const [canAddNotes, setCanAddNotes] = useState<boolean>(false);
  const [isClinical, setIsClinical] = useState<boolean>(false);
  const [allowedFacilityIds, setAllowedFacilityIds] = useState<number[] | null>(null);
  const [roleCheckLoading, setRoleCheckLoading] = useState<boolean>(true);
  const [branchNames, setBranchNames] = useState<Record<string, string>>({});

  // Manual time selection state - Initialize with null to show "Select a time"
  const [selectedTimeSpent, setSelectedTimeSpent] = useState<number | null>(
    null,
  );

  // Time options in minutes
  const timeOptions = [
    { label: "1-5 mins", value: 5 },
    { label: "6-10 mins", value: 10 },
    { label: "11-15 mins", value: 15 },
    { label: "16-20 mins", value: 20 },
    { label: "21-30 mins", value: 30 },
    { label: "31-45 mins", value: 45 },
    { label: "46-60 mins", value: 60 },
  ];

  // Clinical Review Session Timestamps tracking
  const pageOpenedAtRef = useRef<Date>(new Date());
  const [savingEncounterReview, setSavingEncounterReview] = useState<boolean>(false);
  const [encounterReviewSaved, setEncounterReviewSaved] = useState<boolean>(false);

  const saveClinicalReviewTime = async (minutesSpent: number | null) => {
    if (minutesSpent === null) return;

    const activeEncounterId =
      patient?.encounter_id ||
      (patientEncounters.length > 0 ? (patientEncounters[0].encounter_id || (patientEncounters[0] as any).id) : null);

    if (!activeEncounterId) {
      console.warn("No active encounter ID found to record review time");
      return;
    }

    try {
      setSavingEncounterReview(true);
      const user = await getCurrentUser().catch(() => null);
      const reviewStarted = pageOpenedAtRef.current.toISOString();
      const reviewEnded = new Date().toISOString();

      const reviewDurationSeconds = Math.floor(
        (new Date(reviewEnded).getTime() - pageOpenedAtRef.current.getTime()) / 1000,
      );

      const payload = {
        clinical_review_started_at: reviewStarted,
        clinical_review_ended_at: reviewEnded,
        clinical_review_duration: reviewDurationSeconds,  // seconds → postgres column
        encounter_review_status: "reviewed",
        review_recorded_by: user?.username || "Clinical Reviewer",
      };

      await AuthService.updatePatientEncounter(activeEncounterId, payload);

      setPatientEncounters((prev) =>
        prev.map((enc) => {
          if (enc.encounter_id === activeEncounterId || (enc as any).id === activeEncounterId) {
            return { ...enc, ...payload };
          }
          return enc;
        }),
      );

      setEncounterReviewSaved(true);
      setTimeout(() => setEncounterReviewSaved(false), 4000);
      toast.success(`Clinical review time (${minutesSpent} mins) saved to encounter`);
    } catch (err: any) {
      console.error("Failed to save clinical review time to encounter:", err);
      toast.error("Failed to save review time to encounter: " + (err?.message || "Unknown error"));
    } finally {
      setSavingEncounterReview(false);
    }
  };

  const getCallTimes = (log: any) => {
    const startTimeRaw = log.started_at || log.call_started_at || log.created_at;
    const isCompleted = log.status === 'completed';
    const isInProgress = log.status === 'in_progress';
    const isFailed = log.status === 'failed';
    const endTimeRaw = log.ended_at || log.call_ended_at || (isCompleted ? log.updated_at : null);

    const startTimeFormatted = startTimeRaw ? formatDate(startTimeRaw, "h:mm:ss a") : "—";
    let endTimeFormatted = "—";
    if (isInProgress) {
      endTimeFormatted = "In Progress";
    } else if (endTimeRaw && isCompleted) {
      endTimeFormatted = formatDate(endTimeRaw, "h:mm:ss a");
    }

    let durationText = "—";
    const notes = log.discussion_notes || log.notes || "";
    const matchDuration = notes.match(/\[Duration:\s*([^\]]+)\]/i);
    if (matchDuration && matchDuration[1]) {
      durationText = matchDuration[1];
    } else if (log.duration_seconds) {
      const mins = Math.floor(log.duration_seconds / 60);
      const secs = log.duration_seconds % 60;
      durationText = `${mins}m ${secs}s`;
    } else if (startTimeRaw && endTimeRaw && isCompleted) {
      const s = new Date(startTimeRaw).getTime();
      const e = new Date(endTimeRaw).getTime();
      if (!isNaN(s) && !isNaN(e) && e >= s) {
        const diffSec = Math.round((e - s) / 1000);
        const mins = Math.floor(diffSec / 60);
        const secs = diffSec % 60;
        durationText = `${mins}m ${secs}s`;
      }
    } else if (isInProgress) {
      durationText = "Live";
    }

    return {
      startTime: startTimeFormatted,
      endTime: endTimeFormatted,
      duration: durationText,
      isInProgress,
      isCompleted,
      isFailed,
    };
  };

  const toggleAudio = (url: string) => {
    if (playingAudio === url) {
      audioRef.current?.pause();
      setPlayingAudio(null);
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.play();
      setPlayingAudio(url);
      audio.onended = () => setPlayingAudio(null);
    }
  };

  // Consent helper functions
  const getConsentStatus = (
    consentType: string,
  ): {
    hasConsent: boolean;
    consentDate: string | null;
    details: string | null;
  } => {
    if (!patient?.consent_logs) {
      return { hasConsent: false, consentDate: null, details: null };
    }

    const consentLogs = patient.consent_logs
      .filter((log) => log.type === consentType)
      .sort((a, b) => {
        const timeA = new Date(a.recorded_at || a.timestamp).getTime();
        const timeB = new Date(b.recorded_at || b.timestamp).getTime();
        return (isNaN(timeB) ? 0 : timeB) - (isNaN(timeA) ? 0 : timeA);
      });

    if (consentLogs.length === 0) {
      return { hasConsent: false, consentDate: null, details: null };
    }

    const latestConsent = consentLogs[0];
    return {
      hasConsent: latestConsent.status === "accepted",
      consentDate: latestConsent.recorded_at || latestConsent.timestamp,
      details: latestConsent.details,
    };
  };

  const formatConsentType = (type: string) => {
    const typeMap: { [key: string]: string } = {
      hipaa_consent: "HIPAA Consent",
      email_consent: "Email Consent",
      sms_consent: "SMS Consent",
      e_sign_consent: "eSign Consent",
      mhai_consent: "Monitor Health Consent",
      monitorhealth_consent: "Monitor Health Consent",
      remote_monitoring_consent: "Behavioral Health Integration Services Consent",
      biometric_consent: "Biometric Consent",
      audio_consent: "Audio Consent",
      telehealth_appointment_scheduled: "Telehealth Appointment Scheduled",
      in_person_appointment_scheduled: "In-Person Appointment Scheduled",
    };
    return typeMap[type] || type.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
  };

  const getHipaaConsent = () => getConsentStatus("hipaa_consent");
  const getEmailConsent = () => getConsentStatus("email_consent");
  const getSmsConsent = () => getConsentStatus("sms_consent");
  const clean = (str: string) => str?.trim().replace(/\s+/g, "");
  // Helper functions
  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
  };

  const formatTestName = (testName: string | number) => {
    return String(testName);
  };

  const formatTimeFromSeconds = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return `${hours}h ${remainingMinutes}m`;
    }
    return `${minutes} mins`;
  };

  const handleCopyId = (id: string) => {
    navigator.clipboard.writeText(id);
    toast.success("Patient ID copied to clipboard");
  };


  const renderConsentHistory = (logs: ConsentLog[], isAudit: boolean = false) => {
    if (!logs || logs.length === 0) return null;

    // Filter out redundant types as requested by user ("unnecessary duplicate concerns")
    const filteredLogs = logs.filter(log => {
      const type = log.type.toLowerCase();
      // remote_monitoring_consent is redundant with mhai_consent/monitorhealth_consent
      // e_sign and audio are often considered technical metadata in the main view
      if (['remote_monitoring_consent', 'e_sign_consent', 'audio_consent'].includes(type)) return false;
      return true;
    });

    // Deduplicate by type, keeping only the latest log for each type
    const latestLogsMap = new Map<string, ConsentLog>();
    filteredLogs.forEach(log => {
      const existing = latestLogsMap.get(log.type);
      const currentTime = new Date(log.recorded_at || log.timestamp).getTime();
      const existingTime = existing ? new Date(existing.recorded_at || existing.timestamp).getTime() : 0;

      if (!existing || (!isNaN(currentTime) && currentTime > existingTime)) {
        latestLogsMap.set(log.type, log);
      }
    });

    const displayLogs = Array.from(latestLogsMap.values()).sort((a, b) => {
      const timeA = new Date(a.recorded_at || a.timestamp).getTime();
      const timeB = new Date(b.recorded_at || b.timestamp).getTime();
      return (isNaN(timeB) ? 0 : timeB) - (isNaN(timeA) ? 0 : timeA);
    });

    if (displayLogs.length === 0) return null;

    return (
      <div className={isAudit ? "subsection mt-2" : "mt-2"}>
        <h3 className={isAudit ? "text-lg font-bold text-gray-900 mb-4 flex items-center gap-2" : "text-sm font-semibold text-gray-600 mb-4 flex items-center gap-2"}>
          <ShieldCheck className="h-4 w-4 text-[#1a7a4a]" />
          {isAudit ? "Consent History & Audit Trail" : "Detailed Consent Logs"}
        </h3>
        <div className="overflow-hidden border border-gray-100 rounded-2xl shadow-sm">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50/50">
              <tr>
                <th className="px-6 py-3.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                  Date & Time
                </th>
                <th className="px-6 py-3.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                  Consent Type
                </th>
                <th className="px-6 py-3.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {displayLogs.map((log, index) => {
                const date = log.recorded_at || log.timestamp;
                let formattedDate = "N/A";
                try {
                  if (date) {
                    formattedDate = format(parseISO(date), "MMM d, yyyy · h:mm a");
                  }
                } catch (e) {
                  formattedDate = date || "N/A";
                }

                return (
                  <tr key={index} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-medium">
                      {formattedDate}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-bold text-gray-900">
                        {formatConsentType(log.type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold border uppercase tracking-wide ${log.status === "accepted"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : log.status === "rejected"
                            ? "bg-rose-50 text-rose-700 border-rose-200"
                            : "bg-amber-50 text-amber-700 border-amber-200"
                          }`}
                      >
                        {log.status === "accepted" ? "✓ " : "✕ "}
                        {log.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 italic max-w-xs truncate">
                      {log.details || "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Helper functions to get provider and location info
  // Helper function to get provider name from ID
  const getProviderNameFromId = (providerId: string | null): string => {
    if (!providerId) return "Not assigned";

    const cleanId = String(providerId).trim();

    // Check staffUsers with multiple identifiers
    const provider = staffUsers.find((staff) =>
      String(staff.id) === cleanId ||
      (staff.auth_uuid && String(staff.auth_uuid) === cleanId) ||
      (staff.uuid && String(staff.uuid) === cleanId) ||
      (staff.email && String(staff.email).toLowerCase() === cleanId.toLowerCase())
    );

    if (provider) {
      const role = provider.role || provider.staff_role;
      const rawName = provider.name || `${provider.first_name || ""} ${provider.last_name || ""}`.trim() || provider.id;
      const hasDr = rawName.toLowerCase().startsWith("dr.") || rawName.toLowerCase().startsWith("dr ");
      if (role === "Doctor" || hasDr) {
        return hasDr ? rawName : `Dr. ${rawName}`;
      }
      return role ? `${rawName} (${role})` : rawName;
    }
    return "Not assigned";
  };

  const getProviderName = (patientOrEncounter: any): string => {
    if (!patientOrEncounter) return "Not assigned";

    // If explicit name exists
    if (patientOrEncounter.primary_provider_name) {
      const name = patientOrEncounter.primary_provider_name.trim();
      return name.toLowerCase().startsWith("dr.") ? name : `Dr. ${name}`;
    }

    // Direct provider ID on patient or encounter
    const directId = patientOrEncounter.primary_provider_id || patientOrEncounter.primary_provider_uuid;
    if (directId) {
      const resolved = getProviderNameFromId(directId);
      if (resolved && resolved !== "Not assigned") return resolved;
    }

    // If this is a patient, find their encounters
    if (patientEncounters && patientEncounters.length > 0) {
      const encountersWithProviders = patientEncounters.filter(
        (e: any) => e.primary_provider_id || e.primary_provider_uuid,
      );

      if (encountersWithProviders.length > 0) {
        const mostRecent: any = encountersWithProviders.sort(
          (a: any, b: any) =>
            new Date(b.encounter_date || b.created_at || 0).getTime() -
            new Date(a.encounter_date || a.created_at || 0).getTime(),
        )[0];

        const encId = mostRecent?.primary_provider_id || mostRecent?.primary_provider_uuid;
        if (encId) {
          const res = getProviderNameFromId(encId);
          if (res && res !== "Not assigned") return res;
        }
      }
    }

    return "Not assigned";
  };

  const getSecondaryProviderName = (patientOrEncounter: any): string => {
    if (!patientOrEncounter) return "Not assigned";

    // If explicit name exists
    if (patientOrEncounter.secondary_provider_name) {
      const name = patientOrEncounter.secondary_provider_name.trim();
      return name.toLowerCase().startsWith("dr.") ? name : `Dr. ${name}`;
    }

    // Direct provider ID on patient or encounter
    const directId = patientOrEncounter.secondary_provider_id || patientOrEncounter.secondary_provider_uuid;
    if (directId) {
      const resolved = getProviderNameFromId(directId);
      if (resolved && resolved !== "Not assigned") return resolved;
    }

    // If this is a patient, find their encounters
    if (patientEncounters && patientEncounters.length > 0) {
      const encountersWithProviders = patientEncounters.filter(
        (e: any) => e.secondary_provider_id || e.secondary_provider_uuid,
      );

      if (encountersWithProviders.length > 0) {
        const mostRecent: any = encountersWithProviders.sort(
          (a: any, b: any) =>
            new Date(b.encounter_date || b.created_at || 0).getTime() -
            new Date(a.encounter_date || a.created_at || 0).getTime(),
        )[0];

        const encId = mostRecent?.secondary_provider_id || mostRecent?.secondary_provider_uuid;
        if (encId) {
          const res = getProviderNameFromId(encId);
          if (res && res !== "Not assigned") return res;
        }
      }
    }

    return "Not assigned";
  };

  const getBranchLocationName = (encounterOrPatient: any): string => {
    if (!encounterOrPatient) return "Pelham Branch";

    if (typeof encounterOrPatient === "string") return encounterOrPatient;

    if (
      encounterOrPatient.branch_name &&
      encounterOrPatient.branch_name !== "Unknown Location" &&
      encounterOrPatient.branch_name !== "Main Branch"
    ) {
      return encounterOrPatient.branch_name;
    }
    if (encounterOrPatient.associated_branch_name) {
      return encounterOrPatient.associated_branch_name;
    }

    const locIds = encounterOrPatient.location_ids;
    if (locIds && Array.isArray(locIds) && locIds.length > 0) {
      const cleanLocationId = String(locIds[0] || "").replace(/[{}"']/g, "").trim();
      const location = branchLocations.find((loc) => String(loc.id) === cleanLocationId);
      if (location) {
        return location.branch_name || location.name || "Pelham Branch";
      }
    }

    if (encounterOrPatient.facility_id) {
      const facStr = String(encounterOrPatient.facility_id).replace(/[{}"']/g, "").trim();
      const match = branchLocations.find((l: any) => String(l.id) === facStr || String(l.branch_id) === facStr);
      if (match?.branch_name || match?.name) return match.branch_name || match.name;
      if (isNaN(Number(facStr)) && facStr.length > 1) return facStr;
    }

    return "Pelham Branch";
  };

  const getBranchNameForHospital = async (
    encounterOrPatient: any,
  ): Promise<string> => {
    if (!encounterOrPatient) return "Pelham Branch";

    const locIds = encounterOrPatient.location_ids;
    if (!locIds || !Array.isArray(locIds) || locIds.length === 0) {
      return encounterOrPatient.branch_name || encounterOrPatient.associated_branch_name || "Pelham Branch";
    }

    const cleanLocationId = String(locIds[0] || "").replace(/[{}"']/g, "").trim();
    if (!cleanLocationId) return "Pelham Branch";

    try {
      const res = await AuthService.getHospitalsAndBranches();
      if (Array.isArray(res)) {
        for (const g of res) {
          const locs = g.clinic_locations || g.locations || [];
          const found = locs.find((l: any) => String(l.id) === cleanLocationId);
          if (found) {
            return found.name || found.branch_name || "Pelham Branch";
          }
        }
      }
      return "Pelham Branch";
    } catch (error) {
      console.error("❌ Error in getBranchNameForHospital:", error);
      return "Pelham Branch";
    }
  };

  const getPatientAcknowledgementDate = (p: any): string => {
    if (!p) return "Not recorded";
    const date =
      p.acknowledgement_date ||
      p.hipaa_email_and_sms_consent_date ||
      p.consent_date ||
      (Array.isArray(p.consent_logs) && p.consent_logs.length > 0
        ? p.consent_logs[p.consent_logs.length - 1]?.recorded_at ||
        p.consent_logs[0]?.recorded_at ||
        p.consent_logs[0]?.timestamp
        : null) ||
      p.created_at;
    return date ? formatDate(date, "MMM d, yyyy · h:mm a") : "Not recorded";
  };

  const getPatientLocationDisplay = (p: any): string => {
    if (!p) return "In-Person / Pelham Branch";

    // 1. Resolve branch name
    const rawBranch =
      p.branch_name ||
      p.associated_branch_name ||
      (patientEncounters.length > 0 ? getEncounterBranchName(patientEncounters[0]) : null) ||
      (p.location_ids && p.location_ids.length > 0
        ? branchLocations.find((l: any) => String(l.id) === String(p.location_ids[0]).replace(/[{}"']/g, "").trim())?.branch_name
        : null) ||
      getBranchLocationName(p);

    const cleanBranch =
      rawBranch && rawBranch !== "Not assigned" && rawBranch !== "Unknown Location" && rawBranch !== "Main Branch"
        ? rawBranch
        : "Pelham Branch";

    // 2. Check appointment type:
    // First time visit is always In-Person / {branch name}
    // If the appointment was online / TeleHealth -> Virtually (TeleHealth) / {branch name}
    const currentEncounter = patientEncounters.length > 0 ? patientEncounters[0] : null;
    const isOnlineAppointment = currentEncounter && currentEncounter.encounter_type === "TeleHealth";

    if (isOnlineAppointment) {
      return `Virtually (TeleHealth) / ${cleanBranch}`;
    }

    return `In-Person / ${cleanBranch}`;
  };

  const getEncounterBranchName = (encounter: any): string => {
    if (!encounter) return "Pelham Branch";

    // 1. Check location_ids array against loaded branchLocations first
    if (encounter.location_ids && Array.isArray(encounter.location_ids) && encounter.location_ids.length > 0) {
      const cleanLocId = String(encounter.location_ids[0]).replace(/[{}"']/g, "").trim();
      const match = branchLocations.find((l: any) => String(l.id) === cleanLocId);
      if (match?.branch_name) return match.branch_name;
      if (match?.name) return match.name;
    }

    // 2. Check facility_id on encounter
    if (encounter.facility_id) {
      const facStr = String(encounter.facility_id).replace(/[{}"']/g, "").trim();
      const match = branchLocations.find((l: any) => String(l.id) === facStr || String(l.branch_id) === facStr);
      if (match?.branch_name) return match.branch_name;
      if (match?.name) return match.name;
      if (isNaN(Number(facStr)) && facStr.length > 1) {
        return facStr;
      }
    }

    // 3. Direct branch_name or branch object on encounter
    if (
      encounter.branch_name &&
      encounter.branch_name !== "Unknown Location" &&
      encounter.branch_name !== "N/A" &&
      encounter.branch_name !== "Main Branch"
    ) {
      return encounter.branch_name;
    }
    if (encounter.branch?.name || encounter.branch?.branch_name) {
      return encounter.branch.name || encounter.branch.branch_name;
    }

    // 4. Check pre-fetched branchNames map
    if (
      encounter.encounter_id &&
      branchNames[encounter.encounter_id] &&
      branchNames[encounter.encounter_id] !== "Unknown Location" &&
      branchNames[encounter.encounter_id] !== "N/A" &&
      branchNames[encounter.encounter_id] !== "Main Branch"
    ) {
      return branchNames[encounter.encounter_id];
    }

    // 5. Patient branch or default
    return patient?.branch_name || patient?.associated_branch_name || "Pelham Branch";
  };

  const getEncounterHospitalName = (encounter: any): string => {
    if (!encounter) return "Ally Psychiatry";

    // 1. Check location_ids array against loaded branchLocations first
    if (encounter.location_ids && Array.isArray(encounter.location_ids) && encounter.location_ids.length > 0) {
      const cleanLocId = String(encounter.location_ids[0]).replace(/[{}"']/g, "").trim();
      const match = branchLocations.find((l: any) => String(l.id) === cleanLocId);
      if (match?.hospital_name) return match.hospital_name;
    }

    // 2. Check facility_id
    if (encounter.facility_id) {
      const facStr = String(encounter.facility_id).replace(/[{}"']/g, "").trim();
      const match = branchLocations.find((l: any) => String(l.id) === facStr || String(l.branch_id) === facStr);
      if (match?.hospital_name) return match.hospital_name;
    }

    // 3. Direct hospital_name on encounter
    if (
      encounter.hospital_name &&
      encounter.hospital_name !== "Unknown Location" &&
      encounter.hospital_name !== "N/A" &&
      encounter.hospital_name !== "Main Hospital"
    ) {
      return encounter.hospital_name;
    }
    if (encounter.hospital?.name) {
      return encounter.hospital.name;
    }

    return patient?.hospital_name || "Ally Psychiatry";
  };

  const isMostRecentResponse = (response: QuestionnaireResponse): boolean => {
    const responsesForSameTest = questionnaireResponses.filter(
      (r) => r.questionnaire_type === response.questionnaire_type,
    );

    responsesForSameTest.sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );

    return (
      responsesForSameTest.length > 0 &&
      responsesForSameTest[0].id === response.id
    );
  };

  const canEditreviewNote = (response: QuestionnaireResponse): boolean => {
    return isMostRecentResponse(response) && !response.review_notes && canAddNotes;
  };

  const checkUserRole = async () => {
    try {
      setRoleCheckLoading(true);

      let user;
      try {
        user = await getCurrentUser();
      } catch (authError) {
        console.error("Error getting authenticated user:", authError);
        setCurrentUser(null);
        setUserRole(null);
        setIsDoctor(false);
        setCanAddNotes(false);
        setRoleCheckLoading(false);
        return;
      }

      if (!user) {
        setCurrentUser(null);
        setUserRole(null);
        setIsDoctor(false);
        setCanAddNotes(false);
        setRoleCheckLoading(false);
        return;
      }

      setCurrentUser(user);

      const staffData = await AuthService.getProfile(user.username);

      if (!staffData) {
        setUserRole(null);
        setIsDoctor(false);
        setCanAddNotes(false);
        setIsClinical(false);
      } else {
        const foundRole = staffData.role;
        setUserRole(foundRole);
        setIsDoctor(foundRole?.toLowerCase() === "doctor");
        // Collect allowed IDs (location_ids and associated_branch)
        const allowedIdsSet = new Set<string | number>();

        const locIds = staffData.location_ids || staffData.facility_ids;
        if (Array.isArray(locIds)) {
          locIds.forEach(id => allowedIdsSet.add(Number(id)));
        } else if (typeof locIds === 'string') {
          const matches = locIds.match(/\d+/g);
          if (matches) matches.forEach(id => allowedIdsSet.add(Number(id)));
        }

        if (staffData.associated_branch) {
          allowedIdsSet.add(staffData.associated_branch);
          const numBranch = Number(staffData.associated_branch);
          if (!isNaN(numBranch)) allowedIdsSet.add(numBranch);
        }

        setAllowedFacilityIds(Array.from(allowedIdsSet) as any);

        // Fetch overall role metadata to see if this role is clinical
        try {
          const fetchedRoles = await AuthService.getRolesMetadata();
          const roleMeta = fetchedRoles.find(r =>
            (r.name || '').toLowerCase() === (foundRole || '').toLowerCase()
          );
          setIsClinical(roleMeta?.is_clinical === true);
          setCanAddNotes(roleMeta?.is_allowed_to_add_notes === true);
        } catch (roleError) {
          console.error("Error fetching role metadata:", roleError);
          // Fallback to isDoctor if metadata fails
          setIsClinical(foundRole?.toLowerCase() === "doctor");
          setCanAddNotes(["doctor", "nurse practitioner", "physician assistant", "carecoordinator"]
            .includes((foundRole || "").toLowerCase()));
        }
      }

      setRoleCheckLoading(false);
    } catch (error) {
      console.error("Error checking user role:", error);
      // Fallback to auth state role if profile fetch fails
      if (auth?.user?.role) {
        setUserRole(auth.user.role);
        setIsClinical(auth.user.isClinical || false);
        setIsDoctor(auth.user.role.toLowerCase() === 'doctor');
        setCanAddNotes(["doctor", "nurse practitioner", "physician assistant", "carecoordinator"]
          .includes(auth.user.role.toLowerCase()));
      } else {
        setUserRole(null);
        setIsDoctor(false);
        setCanAddNotes(false);
        setIsClinical(false);
      }
      setRoleCheckLoading(false);
    }
  };

  const isSuperAdmin = userRole?.toLowerCase() === 'admin' || currentUser?.role_id === 1;
  const normalizedRole = userRole?.toLowerCase().replace(/_/g, '') || '';
  const isBranchAdmin = normalizedRole === 'branchadmin';
  const isStaff = ['staff', 'technician', 'nurse', 'doctor', 'clinicalstaff', 'clinicalreviewer', 'nutritionist'].includes(normalizedRole);
  const isPatient = normalizedRole === 'patient';
  const isOwnProfile = auth?.user?.username === patient?.auth_uuid || auth?.user?.email === patient?.email;

  const canSeeHighLevel = isSuperAdmin || isClinical || isBranchAdmin || isStaff || (isPatient && isOwnProfile);
  const canSeeDetail = isSuperAdmin || isClinical || isBranchAdmin || isStaff || (isPatient && isOwnProfile);
  const canListenAudio = isSuperAdmin || isClinical || isBranchAdmin || isStaff; // Patients usually shouldn't listen to staff audio logs, but can see their own responses

  const handleOpenEditModal = () => {
    if (patient) {
      console.log("🛠️ Opening Edit Modal. Patient Data:", patient);
      // Strip +1 prefix and format as ###-###-#### for display
      const rawDigits = (patient.mobile_number || "").replace(/\D/g, "").replace(/^1/, "").slice(-10);
      const rawNumber = rawDigits.length > 6
        ? rawDigits.slice(0, 3) + "-" + rawDigits.slice(3, 6) + "-" + rawDigits.slice(6)
        : rawDigits.length > 3
          ? rawDigits.slice(0, 3) + "-" + rawDigits.slice(3)
          : rawDigits;

      // Clean DB artifacts (curly braces, quotes) from text array
      const rawTests = patient.selected_tests || [];
      let parsedTests: string[] = [];
      if (Array.isArray(rawTests)) {
        parsedTests = rawTests.flatMap(t =>
          typeof t === 'string' ? t.replace(/[{}"]/g, '').split(',').map(s => s.trim()) : String(t)
        ).filter(Boolean);
      } else if (typeof rawTests === 'string') {
        parsedTests = (rawTests as string).replace(/[{}"]/g, '').split(',').map(s => s.trim()).filter(Boolean);
      }

      form.reset({
        first_name: patient.first_name,
        last_name: patient.last_name,
        date_of_birth: patient.date_of_birth,
        email: patient.email || "",
        mobile_number: rawNumber,
        selected_tests: parsedTests,
        primary_provider_id: patient.primary_provider_id || (patient as any).primary_cp_uuid || "",
        secondary_provider_id: (patient as any).secondary_provider_id || (patient as any).secondary_cp_uuid || "",
        middle_name: (patient as any).middle_name || "",
        social_security_number: (patient as any).social_security_number || (patient as any).ssn || "",
      });
    }
    setIsEditModalOpen(true);
  };

  // Handle test selection
  const handleTestSelection = (testId: string, isSelected: boolean) => {
    const currentTests = form.getValues("selected_tests") || [];

    if (isSelected) {
      // If already selected, remove it
      const newSelectedTests = currentTests.filter((id) => id !== testId);
      form.setValue("selected_tests", newSelectedTests, { shouldDirty: true });
    } else {
      // If not selected, check if we can add more
      if (currentTests.length >= 2) {
        toast.warning("You can select a maximum of 2 tests");
        return;
      }
      // Add the new test
      const newSelectedTests = [...currentTests, testId];
      form.setValue("selected_tests", newSelectedTests, { shouldDirty: true });
    }
  };

  // Handle save changes
  const handleSaveChanges = async (values: any) => {
    if (!patient) return;
    try {
      // Strip formatting (dashes) and +1 prefix, then re-add +1 in E.164 format
      const rawNumber = (values.mobile_number || "").replace(/\D/g, "").replace(/^1/, "").slice(-10);
      const mobileWithCountryCode = rawNumber ? `+1${rawNumber}` : values.mobile_number;

      // Prepare the update data including selected tests
      const updateData = {
        first_name: values.first_name,
        last_name: values.last_name,
        date_of_birth: values.date_of_birth,
        email: values.email || null,
        mobile_number: mobileWithCountryCode,
        selected_tests: values.selected_tests || [],
        middle_name: values.middle_name || null,
        social_security_number: values.social_security_number || null,
        primary_cp_uuid: values.primary_provider_id,
        secondary_cp_uuid: values.secondary_provider_id === "none" ? null : values.secondary_provider_id,
        enrolled_for_monitorhealth_services: (values.selected_tests && values.selected_tests.length > 0) ? true : patient?.enrolled_for_monitorhealth_services,
      };

      await AuthService.updatePatient(patient.id, updateData);

      // Update providers in the encounter
      if (values.primary_provider_id || values.secondary_provider_id) {
        const encounterUpdate: any = {};
        if (values.primary_provider_id) encounterUpdate.primary_provider_id = values.primary_provider_id;
        if (values.secondary_provider_id) {
          encounterUpdate.secondary_provider_id = values.secondary_provider_id === "none" ? null : values.secondary_provider_id;
        }

        if (patient.encounter_id) {
          await AuthService.updatePatientEncounter(patient.encounter_id, encounterUpdate);
        }
      }

      // Update local state with the new values
      setPatient((prev) => (prev ? {
        ...prev,
        ...updateData,
        primary_provider_id: values.primary_provider_id || (prev as any).primary_provider_id,
        secondary_provider_id: values.secondary_provider_id === "none" ? null : (values.secondary_provider_id || (prev as any).secondary_provider_id)
      } : null));
      setIsEditModalOpen(false);
      toast.success("Patient information updated successfully");
    } catch (error) {
      console.error("Error updating patient:", error);
      toast.error("Failed to update patient information");
    }
  };

  // Face Registration Functions
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
      });
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      toast.error("Failed to access camera: " + err);
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const handleCloseFaceRegistrationDialog = () => {
    isDialogOpenRef.current = false;
    setShowFaceRegistrationModal(false);
    stopCamera();
    setRecordingPhase("initial");
    setStatusMessage("");
    setRecordedVideos([]);
    setRecordingDisabled(false);
    setIsRecording(false);
    setMediaRecorder(null);
    if (recordingTimeoutRef.current) {
      clearTimeout(recordingTimeoutRef.current);
      recordingTimeoutRef.current = null;
    }
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
  };

  const startVideoRecording = async (phase: "initial" | "additional") => {
    if (!cameraStream || !patient) return;
    let options: MediaRecorderOptions = {};
    if (MediaRecorder.isTypeSupported("video/webm;codecs=vp8")) {
      options = {
        mimeType: "video/webm;codecs=vp8",
        videoBitsPerSecond: 400_000,
      };
    } else if (MediaRecorder.isTypeSupported("video/webm;codecs=vp9")) {
      options = {
        mimeType: "video/webm;codecs=vp9",
        videoBitsPerSecond: 400_000,
      };
    } else if (MediaRecorder.isTypeSupported("video/webm")) {
      options = { mimeType: "video/webm", videoBitsPerSecond: 400_000 };
    } else if (MediaRecorder.isTypeSupported("video/mp4")) {
      options = { mimeType: "video/mp4", videoBitsPerSecond: 400_000 };
    } else {
      options = { videoBitsPerSecond: 400_000 };
    }
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(cameraStream, options);
    } catch (err) {
      toast.error("Video recording is not supported in this browser: " + err);
      return;
    }
    let chunks: BlobPart[] = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };
    recorder.onstop = async () => {
      if (!isDialogOpenRef.current) return;
      const blob = new Blob(chunks, { type: recorder.mimeType });
      const fileExt = recorder.mimeType.includes("mp4") ? "mp4" : "webm";
      const file = new File([blob], `recording-${Date.now()}.${fileExt}`, {
        type: recorder.mimeType,
      });
      console.log(
        "Recorded video file size:",
        file.size,
        "bytes (",
        (file.size / 1024 / 1024).toFixed(2),
        "MB )",
      );
      if (phase === "initial") {
        setStatusMessage("Authenticating...");
        const currentUser = await getCurrentUser();
        const username = currentUser?.username;
        if (!username) {
          setStatusMessage("Authentication failed: No active session found");
          toast.error("Authentication failed: No active session found");
          setRecordingDisabled(false);
          return;
        }
        setRecordingDisabled(true);
        // IMPORTANT: Using user.username as it represents the migrated auth_uuid/identifier
        const loginResult = await biometricAuthService.login(username, true);
        if (!loginResult.success) {
          setStatusMessage(
            "Authentication failed: " +
            (loginResult.error?.message || "Unknown error"),
          );
          toast.error(loginResult.error?.message || "Authentication failed");
          setRecordingDisabled(false);
          return;
        }

        setStatusMessage("Checking for accessories...");
        const accessoriesCheck =
          await biometricAuthService.detectAccessories(file);
        console.log("Accessory Detection Result:", accessoriesCheck);
        if (accessoriesCheck?.data?.status === "warning") {
          setStatusMessage(
            "Accessories detected! Please remove them and continue.",
          );
          setRecordingDisabled(false);
          return;
        }

        setStatusMessage("Checking if face is detected...");
        const faceSearch = await biometricAuthService.searchFace(file);
        console.log("Face Search Result:", faceSearch);

        if (faceSearch?.data?.matches && faceSearch.data.matches.length > 0) {
          setStatusMessage(
            "A matching face was found. Registration cannot continue.",
          );
          setExistingFaceData(faceSearch.data.matches[0]);
          setShowFaceFoundDialog(true);
          setRecordingDisabled(false);
          return;
        }

        setStatusMessage(
          "Face detected. Proceeding to additional recording...",
        );
        setRecordedVideos([file]);
        setRecordingPhase("additional");
        setTimeout(() => {
          setRecordingDisabled(false);
          startVideoRecording("additional");
        }, 500);
      } else {
        submitVideos([...recordedVideos, file]);
      }
    };
    const durationMs = phase === "initial" ? 3000 : 10000;
    recorder.start();
    setMediaRecorder(recorder);
    setIsRecording(true);
    setRecordingTime(0);
    setRecordingPhase(phase);
    if (recordingTimeoutRef.current) clearTimeout(recordingTimeoutRef.current);
    recordingTimeoutRef.current = setTimeout(() => {
      if (recorder.state === "recording") {
        recorder.stop();
        setIsRecording(false);
        setMediaRecorder(null);
      }
    }, durationMs);

    // Update recording time
    if (recordingIntervalRef.current)
      clearInterval(recordingIntervalRef.current);
    recordingIntervalRef.current = setInterval(() => {
      setRecordingTime((prev) => {
        const newTime = prev + 1;
        if (newTime >= durationMs / 1000) {
          if (recordingIntervalRef.current)
            clearInterval(recordingIntervalRef.current);
          return durationMs / 1000;
        }
        return newTime;
      });
    }, 1000);
  };

  const stopVideoRecording = () => {
    if (mediaRecorder && mediaRecorder.state === "recording") {
      mediaRecorder.stop();
      mediaRecorder.stream.getTracks().forEach((track) => track.stop());
      setIsRecording(false);
      setMediaRecorder(null);
    }
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
  };

  const submitVideos = async (videos: File[]) => {
    if (!patient) return;
    try {
      const video = videos[videos.length - 1];
      console.log(
        "Submitting video file size:",
        video.size,
        "bytes (",
        (video.size / 1024 / 1024).toFixed(2),
        "MB )",
      );
      const firstName = patient.first_name || "Unknown";
      const lastName = patient.last_name || "User";

      setStatusMessage("Registering face...");
      //Disable the button
      setRecordingDisabled(true);
      const response = await biometricAuthService.addFace(
        video,
        firstName,
        patient.id,
        lastName,
      );

      if (response.success) {
        try {
          await AuthService.updatePatient(patient.id, { is_face_biometric_registered: true });
        } catch (updateError) {
          console.error("Error updating patient in database:", updateError);
          toast.error("Face registered but failed to update database");
          setStatusMessage("Face registered but failed to update database");
          setRecordingDisabled(false);
          return;
        }

        toast.success("Face registered successfully!");
        setPatient((prev) =>
          prev
            ? {
              ...prev,
              is_face_biometric_registered: true,
            }
            : null,
        );
        handleCloseFaceRegistrationDialog();
      } else {
        toast.error(response.error?.message || "Face registration failed");
        setStatusMessage(
          response.error?.message || "Face registration failed",
        );
        setRecordingDisabled(false);
      }
    } catch (error) {
      console.error("Error submitting videos:", error);
      toast.error("Error registering face. Please try again.");
      setStatusMessage("Error registering face. Please try again.");
      setRecordingDisabled(false);
    }
  };

  const handleCallNow = async () => {
    setCallNowLoading(true);
    try {
      // Use questionnaire_id if viewing a response
      const questionnaireId = viewingReviewNote?.response?.id?.toString();
      if (!patient?.id) return;

      // Notify ConnectCCPWidget to open softphone and trigger login if needed
      window.dispatchEvent(
        new CustomEvent("connect:open-phone", {
          detail: {
            patientName: `${patient.first_name || ""} ${patient.last_name || ""}`.trim(),
            patientPhone: patient.phone,
            patientId: patient.id,
          },
        })
      );

      const result = await CallService.makeCallNow(patient.id, questionnaireId);

      if (result.success && result.logId) {
        // Refresh call log
        fetchCallLog(questionnaireId || "");
      }
    } finally {
      setCallNowLoading(false);
    }
  };

  const fetchAllCallLogs = async (patientId: string, responses: any[] = []) => {
    try {
      // 1. Fetch all direct call logs for this patient (including immediate "Call Now" calls)
      const directResult = await AuthService.checkCallLog(undefined, patientId, true);
      const directLogs: any[] = directResult?.logs || (directResult?.log ? [directResult.log] : []);

      // 2. Also check questionnaire-specific logs if questionnaire responses exist
      let qLogs: any[] = [];
      if (responses && responses.length > 0) {
        const logPromises = responses.map(r => AuthService.checkCallLog(r.id, patientId));
        const results = await Promise.all(logPromises);
        qLogs = results
          .filter(res => res?.exists && res?.log)
          .map(res => res.log);
      }

      // Merge and deduplicate by call log id
      const allMerged = [...directLogs, ...qLogs];
      const seenIds = new Set();
      const uniqueLogs = allMerged.filter(log => {
        if (!log || !log.id) return false;
        if (seenIds.has(log.id)) return false;
        seenIds.add(log.id);
        return true;
      });

      setAllCallLogs(uniqueLogs);
      return uniqueLogs;
    } catch (error) {
      console.error("Error fetching all call logs:", error);
      return [];
    }
  };

  const fetchCallLog = async (questionnaireId: string) => {
    if (!questionnaireId) return;
    setFetchingCallLog(true);
    try {
      if (!patient?.id) return;
      const result = await AuthService.checkCallLog(questionnaireId, patient.id);
      if (result.exists) {
        setCurrentCallLog(result.log);
      } else {
        setCurrentCallLog(null);
      }
    } catch (error) {
      console.error("Error fetching call log:", error);
    } finally {
      setFetchingCallLog(false);
    }
  };

  useEffect(() => {
    if (viewingReviewNote?.response?.id) {
      fetchCallLog(viewingReviewNote.response.id.toString());
    } else {
      setCurrentCallLog(null);
    }
  }, [viewingReviewNote]);

  const handleScheduleCall = async () => {
    if (!scheduleCallDate || !scheduleCallTime) {
      toast.error("Please select both date and time");
      return;
    }

    const todayStr = new Date().toISOString().split('T')[0];
    if (scheduleCallDate <= todayStr) {
      toast.error("Appointments cannot be scheduled for today. Please select tomorrow or a future date.");
      return;
    }

    // Create date from local inputs — stored as-is in local time, no offset applied
    const date = new Date(`${scheduleCallDate}T${scheduleCallTime}`);

    const scheduledDateTime = date.toISOString();
    const questionnaireId = viewingReviewNote?.response?.id?.toString();

    if (!patient?.id) return;
    const result = await CallService.scheduleCall(patient.id, scheduledDateTime, questionnaireId);
    if (result.success) {
      setIsScheduleCallModalOpen(false);
      setScheduleCallDate("");
      setScheduleCallTime("");

      // Refresh all patient data and call logs
      if (questionnaireId) {
        await fetchCallLog(questionnaireId.toString());
      }
      // Re-fetch patient data to update all lists/tables
      fetchPatientData(patient.id);
    }
  };

  const [fetchingTranscript, setFetchingTranscript] = useState(false);
  const handleFetchTranscript = async () => {
    if (!currentCallLog?.id) return;
    setFetchingTranscript(true);
    try {
      const res = await AuthService.fetchCallNotes(currentCallLog.id);
      if (res.transcript_ready) {
        toast.success("Transcript retrieved successfully");
        fetchCallLog(viewingReviewNote?.response?.id?.toString() || "");
      } else {
        toast.info(res.hint || "Transcript not ready yet. Please try again in a few minutes.");
      }
    } catch (error) {
      console.error("Error fetching transcript:", error);
      toast.error("Failed to fetch transcript");
    } finally {
      setFetchingTranscript(false);
    }
  };

  const isLatestResponse = (responseId: string) => {
    if (!questionnaireResponses.length) return false;
    const sorted = [...questionnaireResponses].sort((a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    return sorted[0].id.toString() === responseId.toString();
  };

  const getCallNowStatus = () => {
    // User requested: do not wait for the consent just display call now button once call is schedule and time is near
    // Removed: if (!smsConsent.hasConsent) return { canCall: false, reason: "No Consent" };

    if (!currentCallLog?.scheduled_at) return { canCall: false, reason: "No Scheduled Call" };
    if (currentCallLog.status === 'completed') return { canCall: false, reason: "Already Completed" };

    const scheduledTime = new Date(currentCallLog.scheduled_at).getTime();
    const currentTime = Date.now();
    // Allow call if it's within 10 minutes before scheduled time up to 60 minutes after
    const tenMinutesBefore = 10 * 60 * 1000;
    const sixtyMinutesAfter = 60 * 60 * 1000;

    if (currentTime < (scheduledTime - tenMinutesBefore)) return { canCall: false, reason: "Too Early" };
    if (currentTime > (scheduledTime + sixtyMinutesAfter)) return { canCall: false, reason: "Expired" };

    return { canCall: true };
  };

  // Returns true when a call log exists but the window has fully expired —
  // used to show a "Reschedule" option so the provider isn't stuck.
  const isCallExpired = () => {
    if (!currentCallLog?.scheduled_at) return false;
    if (currentCallLog.status === 'completed') return false;
    const scheduledTime = new Date(currentCallLog.scheduled_at).getTime();
    return Date.now() > scheduledTime + 60 * 60 * 1000;
  };

  // Initialize camera when modal opens
  useEffect(() => {
    if (showFaceRegistrationModal) {
      startCamera();
      isDialogOpenRef.current = true;
    } else {
      stopCamera();
    }
    return () => {
      stopCamera();
    };
  }, [showFaceRegistrationModal]);

  useEffect(() => {
    const fetchQuestionnaireTemplates = async () => {
      try {
        const data = await AuthService.getQuestionnaireTemplates({ is_active: true });
        setQuestionnaireTemplates(
          (data || []).map((template: any) => ({
            id: template.id || template.template_id || template.id,
            name: template.full_name || template.name || template.template_name || template.id,
            age_min: template.age_min,
            age_max: template.age_max,
            source_citations: template.source_citations,
          })),
        );
      } catch (error) {
        console.error("Error fetching questionnaire templates:", error);
        toast.error("Failed to load test templates");
      } finally {
        setTemplatesLoading(false);
      }
    };
    fetchQuestionnaireTemplates();
  }, []);

  const fetchPatientData = async (patientId: string) => {
    try {
      setLoading(true);
      return await AuthService.getPatient(patientId);
    } catch (error) {
      console.error("Error fetching patient data:", error);
      return null;
    }
  };

  const fetchQuestionnaireResponses = async (patientId: string) => {
    try {
      const res = await AuthService.getQuestionnaireResponses({ patient_id: patientId }) as any;
      const responses = Array.isArray(res) ? res : (res?.data || []) as any[];

      if (!responses?.length) return [];
      const reviewedByIds = [...new Set(responses.map((r) => r.reviewed_by).filter(Boolean))];
      const staffMap = new Map<string, string>();
      for (const id of reviewedByIds) {
        const staff = await AuthService.getProfile(id);
        if (staff?.name) staffMap.set(id, staff.name);
      }
      return responses.map((r) => ({
        ...r,
        reviewed_by_name: r.reviewed_by ? staffMap.get(r.reviewed_by) || "Unknown User" : null,
      }));
    } catch (error) {
      console.error("Error fetching questionnaire responses:", error);
      return [];
    }
  };

  const fetchPatientEncounters = async (patientId: string) => {
    try {
      const data = await AuthService.getPatientEncounters({ patient_id: patientId });
      return (data || []).sort((a: any, b: any) =>
        new Date(b.encounter_date || 0).getTime() - new Date(a.encounter_date || 0).getTime()
      );
    } catch (error) {
      console.error("Error fetching patient encounters:", error);
      return [];
    }
  };

  const fetchSmsLogs = async (patientId: string) => {
    try {
      const data = await AuthService.getSMSLogsByPatient(patientId);
      return (data || []).sort((a: any, b: any) =>
        new Date(b.updated_at || b.created_at || 0).getTime() - new Date(a.updated_at || a.created_at || 0).getTime()
      );
    } catch (error) {
      console.error("Error fetching SMS logs:", error);
      return [];
    }
  };

  const fetchStaffUsers = async (locationId?: string | number) => {
    try {
      const data = await AuthService.getProviders(
        locationId ? { location_id: locationId } : undefined,
      );
      const list = Array.isArray(data) ? data : (data as any)?.data || [];
      if (list.length === 0 && locationId) {
        const all = await AuthService.getProviders();
        return Array.isArray(all) ? all : (all as any)?.data || [];
      }
      return list;
    } catch (error) {
      console.error("Error fetching staff users:", error);
      return [];
    }
  };

  const fetchCmsAlerts = async (patientId: string) => {
    try {
      setCmsAlertsLoading(true);
      const alerts = await AuthService.getPatientCmsAlerts(patientId);
      setCmsAlerts(alerts || []);
    } catch (err) {
      console.error("Error fetching patient CMS alerts:", err);
    } finally {
      setCmsAlertsLoading(false);
    }
  };

  const handleCmsAcknowledge = async (alertId: number) => {
    try {
      setCmsAlertActionLoading(alertId);
      const res = await AuthService.acknowledgeCmsAlert(alertId);
      if (res?.success) {
        toast.success("Alert acknowledged");
        if (id) fetchCmsAlerts(id);
      } else {
        toast.error("Failed to acknowledge alert");
      }
    } catch (err) {
      console.error("Error acknowledging CMS alert:", err);
    } finally {
      setCmsAlertActionLoading(null);
    }
  };

  const handleCmsDismiss = async (alertId: number) => {
    try {
      setCmsAlertActionLoading(alertId);
      const res = await AuthService.dismissCmsAlert(alertId);
      if (res?.success) {
        toast.success("Alert dismissed");
        if (id) fetchCmsAlerts(id);
      } else {
        toast.error("Failed to dismiss alert");
      }
    } catch (err) {
      console.error("Error dismissing CMS alert:", err);
    } finally {
      setCmsAlertActionLoading(null);
    }
  };

  const handleCmsResolve = async () => {
    if (!cmsResolveModalId) return;
    if (!cmsResolveNotes.trim()) {
      toast.error("Resolution notes are required");
      return;
    }

    try {
      setCmsAlertActionLoading(cmsResolveModalId);
      const res = await AuthService.resolveCmsAlert(cmsResolveModalId, cmsResolveNotes.trim());
      if (res?.success) {
        toast.success("Alert resolved");
        setCmsResolveModalId(null);
        setCmsResolveNotes("");
        if (id) fetchCmsAlerts(id);
      } else {
        toast.error("Failed to resolve alert");
      }
    } catch (err) {
      console.error("Error resolving CMS alert:", err);
    } finally {
      setCmsAlertActionLoading(null);
    }
  };

  const fetchBranchLocations = async () => {
    try {
      const res = await AuthService.getHospitalsAndBranches();
      if (Array.isArray(res)) {
        return res.flatMap((g: any) => {
          const hName = g.hospital_name || g.hospitalName || "Ally Psychiatry";
          const locs = g.clinic_locations || g.locations || [];
          return locs.map((loc: any) => {
            const bName = loc.name || loc.branch_name || loc.branchName || "Pelham Branch";
            const hospitalName = loc.hospital_name || loc.hospitalName || hName || "Ally Psychiatry";
            return {
              ...loc,
              id: loc.id,
              name: bName,
              branch_name: bName,
              hospital_name: hospitalName,
              hospital_id: loc.hospital_id || g.hospital_id,
            };
          });
        });
      }
      return (res as any)?.locations || (res as any)?.clinic_locations || [];
    } catch (error) {
      console.error("Error fetching branch locations:", error);
      return [];
    }
  };

  const processQuestionnaireData = (incomingResponses: QuestionnaireResponse[]) => {
    const responses = Array.isArray(incomingResponses) ? incomingResponses : [];
    try {
      if (responses.length === 0) {
        if (incomingResponses && !Array.isArray(incomingResponses)) {
          console.error("processQuestionnaireData: responses is not an array", incomingResponses);
        }
        setChartData([]);
        setTestNames([]);
        setSelectedTests([]);
        return;
      }

      const groupedByType: { [key: string]: QuestionnaireResponse[] } = {};

      responses.forEach((response) => {
        const type = response.questionnaire_type;
        if (!groupedByType[type]) {
          groupedByType[type] = [];
        }
        groupedByType[type].push(response);
      });

      const tests = Object.keys(groupedByType);
      setTestNames(tests);
      setSelectedTests(tests);

      const responsesByDate: { [key: string]: QuestionnaireResponse[] } = {};

      responses.forEach((response) => {
        const dateStr = response.created_at.split("T")[0];
        if (!responsesByDate[dateStr]) {
          responsesByDate[dateStr] = [];
        }
        responsesByDate[dateStr].push(response);
      });

      const sortedDates = Object.keys(responsesByDate).sort();
      const chartData: ChartDataPoint[] = sortedDates.map((date) => {
        const dataPoint: ChartDataPoint = { date };

        tests.forEach((test) => {
          const responsesForTestOnDate = responsesByDate[date]
            .filter((r) => r.questionnaire_type === test)
            .sort(
              (a, b) =>
                new Date(b.created_at).getTime() -
                new Date(a.created_at).getTime(),
            );

          if (responsesForTestOnDate.length > 0) {
            const latestResponse = responsesForTestOnDate[0];
            dataPoint[test] = latestResponse.total_score;

            dataPoint[`${test}_response`] = {
              ...latestResponse,
              review_notes: latestResponse.review_notes || null,
              reviewed_by: latestResponse.reviewed_by || null,
              reviewed_by_name: latestResponse.reviewed_by_name || null,
              reviewed_at: latestResponse.reviewed_at || null,
              time_spent_on_review: latestResponse.time_spent_on_review || null,
            };
          }
        });

        return dataPoint;
      });

      setChartData(chartData);
    } catch (error) {
      console.error("Error processing questionnaire data:", error);
      setChartData([]);
      setTestNames([]);
      setSelectedTests([]);
    }
  };

  const generateAuditReport = async () => {
    setAuditReportLoading(true);
    try {
      const [encounters, smsData, callLogs, staffData, locationData] = await Promise.all([
        fetchPatientEncounters(id),
        fetchSmsLogs(id),
        fetchAllCallLogs(id, questionnaireResponses),
        fetchStaffUsers(patient?.branch_id || patient?.facility_id),
        fetchBranchLocations(),
      ]);

      console.log("✅ Data fetch complete:", {
        encounters: encounters.length,
        staffData: staffData.length,
        locationData: locationData.length,
      });

      // Debug: Check if staff data is available
      if (staffData.length > 0) {
        console.log("📋 Staff data sample:", staffData.slice(0, 3));
      } else {
        console.log("❌ No staff data loaded");
      }

      setPatientEncounters(encounters);

      // Fetch branch names for all encounters
      console.log("Fetching branch names...");
      const branchNamesMap: Record<string, string> = {};
      for (const encounter of encounters) {
        if (
          encounter.location_ids &&
          Array.isArray(encounter.location_ids) &&
          encounter.location_ids.length > 0 &&
          encounter.encounter_id
        ) {
          console.log(
            ">>>>>>>>>>>>. Fetching branch name for location_ids:",
            encounter.location_ids[0],
          );
          const name = await getBranchNameForHospital(encounter);
          branchNamesMap[encounter.encounter_id] = name;
        }
      }
      setBranchNames(branchNamesMap);

      setSmsLogs(smsData);
      setAllCallLogs(callLogs);
      setStaffUsers(staffData);
      setBranchLocations(locationData);
      setShowAuditReport(true);
    } catch (error) {
      console.error("Error generating review report:", error);
      setError("Failed to generate review report. Please try again.");
    } finally {
      setAuditReportLoading(false);
    }
  };

  const printAuditReport = () => {
    const printContent = document.getElementById("audit-report-content");
    if (!printContent) return;

    const originalTitle = document.title;
    document.title = `Patient Enrollment Report - ${patient?.first_name}${patient?.middle_name ? ' ' + patient.middle_name : ''} ${patient?.last_name}`;

    // Create comprehensive print-specific styles
    const printStyles = `
    <style>
      /* CRITICAL: Override any height restrictions that break pagination */
      html, body {
        height: auto !important;
        min-height: auto !important;
        overflow: visible !important;
        margin: 0 !important;
        padding: 0 !important;
        background: white !important;
      }

      @media print {
        body {
          padding: 1.5cm;
          font-family: Arial, sans-serif;
          font-size: 11pt;
          line-height: 1.5;
          color: #000;
        }

        /* Ensure background colors are printed (User must still check 'Background graphics' in print dialog) */
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        /* Page settings */
        @page {
          margin: 1.5cm;
          size: A4 portrait;
        }

        /* Hide interactive elements */
        button, .no-print {
          display: none !important;
        }

        /* Force block layout for sections to avoid grid overlap */
        .section {
          display: block !important;
          width: 100% !important;
          page-break-inside: auto !important;
          margin-bottom: 25px !important;
        }

        /* Convert grids to flex for better print wrapping */
        .grid {
          display: flex !important;
          flex-wrap: wrap !important;
          gap: 15px !important;
        }

        .grid-cols-2 > * { width: calc(50% - 10px) !important; }
        .grid-cols-3 > * { width: calc(33.33% - 10px) !important; }
        .md\\:grid-cols-2 > * { width: calc(50% - 10px) !important; }
        .lg\\:grid-cols-3 > * { width: calc(33.33% - 10px) !important; }

        /* Table Pagination Fixes */
        table {
          width: 100% !important;
          border-collapse: collapse !important;
          page-break-inside: auto !important;
          margin-bottom: 20px;
        }

        tr {
          page-break-inside: avoid !important;
          page-break-after: auto !important;
        }

        thead {
          display: table-header-group !important;
        }

        /* Header styling */
        .header {
          text-align: center;
          margin-bottom: 30px;
          border-bottom: 2px solid #1a7a4a;
          padding-bottom: 20px;
        }

        .section-title {
          font-size: 14pt;
          font-weight: bold;
          color: #1a7a4a !important;
          margin-bottom: 15px;
          border-bottom: 1px solid #eee;
          padding-bottom: 5px;
        }
      }
    </style>
    `;

    const printWindow = window.open("", "", "width=800,height=600");
    if (printWindow) {
      // Get all stylesheets from the parent document
      const styles = Array.from(document.styleSheets)
        .map((styleSheet) => {
          try {
            return Array.from(styleSheet.cssRules)
              .map((rule) => rule.cssText)
              .join("\n");
          } catch (e) {
            // Handle CORS issues with external stylesheets
            console.warn("Could not access stylesheet:", e);
            return "";
          }
        })
        .join("\n");

      printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${document.title}</title>
        <style>${styles}</style>
        ${printStyles}
      </head>
      <body>
        ${printContent.innerHTML}
      </body>
      </html>
    `);
      printWindow.document.close();

      // Wait for content and images to load before printing
      printWindow.onload = function () {
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
          printWindow.close();
          document.title = originalTitle;
        }, 250);
      };
    } else {
      document.title = originalTitle;
    }
  };

  const handleOpenEmailReportModal = () => {
    if (!patient) return;
    const primaryName = getProviderName(patient);
    const secondaryName = getSecondaryProviderName(patient);

    const primaryDoc = staffUsers.find(
      (s) =>
        String(s.id) === String(patient.primary_provider_id || patient.primary_provider_uuid) ||
        (s.auth_uuid && String(s.auth_uuid) === String(patient.primary_provider_id || patient.primary_provider_uuid)),
    );

    let secondaryDoc: (typeof staffUsers)[0] | undefined;
    const directSecId = (patient as any).secondary_provider_id || (patient as any).secondary_provider_uuid;
    if (directSecId) {
      secondaryDoc = staffUsers.find(
        (s) =>
          String(s.id) === String(directSecId) ||
          (s.auth_uuid && String(s.auth_uuid) === String(directSecId)) ||
          (s.uuid && String(s.uuid) === String(directSecId)),
      );
    }
    if (!secondaryDoc && patientEncounters.length > 0) {
      const encWithSec = [...patientEncounters]
        .filter((e: any) => e.secondary_provider_id || e.secondary_provider_uuid)
        .sort(
          (a: any, b: any) =>
            new Date(b.encounter_date || b.created_at || 0).getTime() -
            new Date(a.encounter_date || a.created_at || 0).getTime(),
        );
      if (encWithSec.length > 0) {
        const secId = (encWithSec[0] as any).secondary_provider_id || (encWithSec[0] as any).secondary_provider_uuid;
        secondaryDoc = staffUsers.find(
          (s) =>
            String(s.id) === String(secId) ||
            (s.auth_uuid && String(s.auth_uuid) === String(secId)) ||
            (s.uuid && String(s.uuid) === String(secId)),
        );
      }
    }

    const formattedPrimaryName = primaryName.startsWith("Dr.") ? primaryName : `Dr. ${primaryName}`;
    setEmailModalPrimaryName(formattedPrimaryName);
    setEmailModalPrimaryEmail(primaryDoc?.email || "");
    setEmailModalPrimarySelected(true);

    const hasSec = secondaryName !== "Not assigned";
    setEmailModalHasSecondary(hasSec);
    if (hasSec) {
      const formattedSecName = secondaryName.startsWith("Dr.") ? secondaryName : `Dr. ${secondaryName}`;
      setEmailModalSecondaryName(formattedSecName);
      setEmailModalSecondaryEmail(secondaryDoc?.email || "");
      setEmailModalSecondarySelected(!!secondaryDoc?.email);
    } else {
      setEmailModalSecondaryName("");
      setEmailModalSecondaryEmail("");
      setEmailModalSecondarySelected(false);
    }

    setEmailModalCustomSelected(false);
    setEmailModalCustomName("");
    setEmailModalCustomEmail("");
    setIsEmailReportModalOpen(true);
  };

  const handleSendEmailReport = async () => {
    if (!patient) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const recipientsToSend: { title: string; email: string }[] = [];

    if (emailModalPrimarySelected) {
      const trimmedEmail = emailModalPrimaryEmail.trim();
      if (!trimmedEmail) {
        toast.error("Please enter an email address for the Primary Provider.");
        return;
      }
      if (!emailRegex.test(trimmedEmail)) {
        toast.error(`"${trimmedEmail}" is not a valid email address for Primary Provider.`);
        return;
      }
      recipientsToSend.push({
        title: emailModalPrimaryName.trim() || "Care Provider",
        email: trimmedEmail,
      });
    }

    if (emailModalHasSecondary && emailModalSecondarySelected) {
      const trimmedEmail = emailModalSecondaryEmail.trim();
      if (!trimmedEmail) {
        toast.error("Please enter an email address for the Secondary Provider.");
        return;
      }
      if (!emailRegex.test(trimmedEmail)) {
        toast.error(`"${trimmedEmail}" is not a valid email address for Secondary Provider.`);
        return;
      }
      recipientsToSend.push({
        title: emailModalSecondaryName.trim() || "Care Provider",
        email: trimmedEmail,
      });
    }

    if (emailModalCustomSelected) {
      const trimmedEmail = emailModalCustomEmail.trim();
      if (!trimmedEmail) {
        toast.error("Please enter an email address for the Additional Recipient.");
        return;
      }
      if (!emailRegex.test(trimmedEmail)) {
        toast.error(`"${trimmedEmail}" is not a valid email address for Additional Recipient.`);
        return;
      }
      recipientsToSend.push({
        title: emailModalCustomName.trim() || "Recipient",
        email: trimmedEmail,
      });
    }

    if (recipientsToSend.length === 0) {
      toast.warning("Please select at least one recipient to receive the report.");
      return;
    }

    try {
      setEmailSending(true);
      const branchName = getPatientLocationDisplay(patient);
      const patientFullName = `${patient.first_name || ""}${patient.middle_name ? " " + patient.middle_name : ""} ${patient.last_name || ""}`.trim();
      const dobFormatted = patient.date_of_birth ? formatDate(patient.date_of_birth) : "N/A";
      const nowFormatted =
        new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
        " · " +
        new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

      const primaryName = getProviderName(patient);
      const secondaryName = getSecondaryProviderName(patient);

      for (const { title, email } of recipientsToSend) {
        const subject = `New Patient Registration & Behavioral Health Integration Services Consent for ${title} as care provider`;
        const html = buildRegistrationEmailHtml({
          providerName: title,
          patientId: String(patient.id),
          patientName: patientFullName,
          dob: dobFormatted,
          gender: patient.gender || "Not specified",
          mobile: formatPhoneDisplay(patient.mobile_number) || "N/A",
          email: patient.email || "",
          registrationDate: patient.created_at ? formatDate(patient.created_at) : nowFormatted,
          acknowledgementDate: patient.created_at ? formatDate(patient.created_at) : nowFormatted,
          location: branchName,
          primaryProviderName: primaryName.startsWith("Dr.") ? primaryName : `Dr. ${primaryName}`,
          secondaryProviderName: secondaryName !== "Not assigned" ? secondaryName : undefined,
        });

        await EmailService.sendEmail({
          to: email,
          subject,
          body: html,
          from: "noreply@monitorhealth.ai",
          from_email: "noreply@monitorhealth.ai",
          sender_name: "MonitorHealth AI",
          from_name: "MonitorHealth AI",
          reply_to: "noreply@monitorhealth.ai",
        });
      }

      toast.success(`Patient Logs Report successfully emailed to: ${recipientsToSend.map((r) => r.email).join(", ")}`);
      setIsEmailReportModalOpen(false);
    } catch (err: any) {
      console.error("Failed to send email:", err);
      toast.error("Failed to send report: " + (err?.message || "Unknown error"));
    } finally {
      setEmailSending(false);
    }
  };

  const savereviewNote = async () => {
    if (!editingResponse || !patient) return;

    // Check if time is selected
    if (selectedTimeSpent === null) {
      toast.error("Please select the time spent on review");
      return;
    }

    try {
      const user = await getCurrentUser();

      const timeInSeconds = selectedTimeSpent * 60;

      await AuthService.updateQuestionnaireResponse(String(editingResponse.id), {
        review_notes: reviewNote,
        time_spent_on_review: timeInSeconds,
        reviewed_by: user?.username, // Use username instead of userId for migrated RDS
        reviewed_at: new Date().toISOString(),
        status: "reviewed",
      });

      // Also persist clinical review start/end timestamps and review time to patient encounter
      await saveClinicalReviewTime(selectedTimeSpent);

      const updatedResponses = await fetchQuestionnaireResponses(id);
      setQuestionnaireResponses(updatedResponses);
      processQuestionnaireData(updatedResponses);

      setEditingResponse(null);
      setReviewNote("");
      setSelectedTimeSpent(null); // Reset to null
      toast.success("Review note saved successfully");
    } catch (error) {
      console.error("Error saving review note:", error);
      setError("Failed to save review note. Please try again.");
      toast.error("Failed to save review note");
    }
  };

  useEffect(() => {
    const fetchQuestionnaireTemplates = async () => {
      try {
        const data = await AuthService.getQuestionnaireTemplates({ is_active: true });
        setAvailableTests((data || []).map((t: any) => t.id));
      } catch (err) {
        console.error("Error fetching questionnaire templates:", err);
      }
    };

    fetchQuestionnaireTemplates();
  }, []);

  // Main data loading effect
  useEffect(() => {
    if (id) {
      checkUserRole().then(() => {
        Promise.all([
          fetchPatientData(id),
          fetchQuestionnaireResponses(id),
          fetchPatientEncounters(id),
          fetchBranchLocations(),
        ])
          .then(async ([patientData, responsesData, encountersData, locationsData]) => {
            if (locationsData) setBranchLocations(locationsData);
            if (patientData) {
              setPatientEncounters(encountersData);
              // Access control check for BranchAdmin/Admin
              const role = userRole?.toLowerCase();
              const isSuperAdmin =
                role === "admin" || currentUser?.role_id === 1;

              if (!isSuperAdmin && allowedFacilityIds) {
                const patientBranchId = Number(
                  patientData.branch_id || patientData.facility_id,
                );
                if (
                  patientBranchId &&
                  !allowedFacilityIds.includes(patientBranchId)
                ) {
                  setError(
                    "You do not have permission to view patients in this branch.",
                  );
                  setLoading(false);
                  return;
                }
              }

              setPatient(patientData);
              setQuestionnaireResponses(responsesData);
              if (id) fetchCmsAlerts(id);

              // Fetch providers for the patient's branch using the same API as ProfileForm
              const branchId = patientData.branch_id || patientData.facility_id;
              const staffData = await fetchStaffUsers(branchId);
              setStaffUsers(staffData);

              processQuestionnaireData(responsesData);
              fetchAllCallLogs(id, responsesData);
              setLoading(false);
            } else {
              setError("Failed to load patient data");
              setLoading(false);
            }
          })
          .catch((error) => {
            console.error("Error loading patient data:", error);
            setError("Failed to load patient data");
            setLoading(false);
          });
      });
    }
  }, [id]);

  // Auto-open audit report when navigated with ?openReport=true
  useEffect(() => {
    if (patient && !loading) {
      const searchParams = new URLSearchParams(window.location.search);
      if (searchParams.get("openReport") === "true") {
        generateAuditReport();
      }
    }
  }, [patient, loading]);

  const CustomTooltip = useCallback(
    ({
      active,
      payload,
      label,
    }: {
      active?: boolean;
      payload?: Array<{
        payload: Record<string, any>;
        dataKey: string;
        value: number;
        color: string;
      }>;
      label?: string;
    }) => {
      if (active && payload && payload.length) {
        const data = payload[0].payload;
        const testName = payload[0].dataKey;
        const score = payload[0].value;
        const responseKey = `${testName}_response`;
        const response = data[responseKey] as QuestionnaireResponse;

        // Fallback to data.date if label is missing
        const displayLabel = label || data.date;

        const handleTooltipClick = (e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          if (response) {
            setViewingReviewNote({ response });
          }
        };

        return (
          <div
            className="bg-white p-4 border border-gray-200 rounded shadow-lg max-w-xs cursor-pointer hover:shadow-xl transition-shadow"
            onClick={handleTooltipClick}
            style={{ zIndex: 1000 }}
          >
            <p className="font-semibold">{formatDate(displayLabel)}</p>
            <p className="text-blue-600">Test: {formatTestName(testName)}</p>
            <p className="font-medium">Score: {score}</p>

            {response && response.review_notes ? (
              <div className="mt-2">
                <p className="font-medium text-sm text-green-700">
                  ✓ Audit Note Available
                </p>
                {response.time_spent_on_review && (
                  <p className="text-xs text-gray-600">
                    Review time:{" "}
                    {formatTimeFromSeconds(response.time_spent_on_review)}
                  </p>
                )}
                <p className="text-xs text-blue-600 mt-1 font-medium">
                  👆 Click to view full review log
                </p>
              </div>
            ) : response && canEditreviewNote(response) ? (
              <div className="mt-2">
                <p className="font-medium text-sm text-orange-600">
                  ⚠ No Audit Note
                </p>
                <p className="text-xs text-blue-600 mt-1 font-medium">
                  👆 Click to add review note
                </p>
              </div>
            ) : response &&
              isMostRecentResponse(response) &&
              !response.review_notes &&
              !canAddNotes ? (
              <div className="mt-2">
                <p className="font-medium text-sm text-red-600">
                  🚫 Access Required
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Only clinical staff with note permissions can add review notes
                </p>
              </div>
            ) : (
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  No review notes available
                </p>
                <p className="text-xs text-blue-600 mt-1 font-medium">
                  👆 Click to view details
                </p>
              </div>
            )}

            <div className="mt-2 pt-2 border-t border-gray-100">
              <p className="text-xs text-gray-400 italic">
                Click on the data point to view details
              </p>
            </div>
          </div>
        );
      }
      return null;
    },
    [canEditreviewNote, isMostRecentResponse, canAddNotes],
  );

  const memoizedChartData = useMemo(() => chartData, [chartData]);
  const memoizedSelectedTests = useMemo(() => selectedTests, [selectedTests]);

  const ChartComponent = useMemo(() => {
    if (!memoizedChartData.length) return null;

    return (
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={memoizedChartData}
          margin={{
            top: 10,
            right: 30,
            left: 60,
            bottom: 20,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            padding={{ left: 50, right: 50 }}
            tick={{ fontSize: 12 }}
            height={50}
            angle={-45}
            textAnchor="end"
            interval={0}
            tickFormatter={(value) => {
              try {
                return format(parseISO(value), "MMM d");
              } catch {
                return value;
              }
            }}
          />
          <YAxis
            label={{
              value: "Score",
              angle: -90,
              position: "insideLeft",
              style: { textAnchor: "middle" },
            }}
            domain={[0, 27]}
            ticks={[0, 4, 9, 14, 19, 27]}
            width={100}
            tick={{ fontSize: 11 }}
          />
          <Tooltip
            content={<CustomTooltip />}
            cursor={{ stroke: "#8884d8", strokeWidth: 2 }}
            allowEscapeViewBox={{ x: false, y: false }}
            position={{ x: undefined, y: undefined }}
            wrapperStyle={{ zIndex: 1000, pointerEvents: 'auto' }}
          />
          <Legend />
          {(activeTab === "all" ? memoizedSelectedTests : [activeTab]).map(
            (test, index) => (
              <Fragment key={`group-${test}`}>
                {/* Invisible Bar to ensure a hover/click area exists even for single data points */}
                <Bar dataKey={test} fill="transparent" barSize={40} />
                <Line
                  type="monotone"
                  dataKey={test}
                  stroke={`hsl(${(index * 137.5) % 360}, 70%, 50%)`}
                  strokeWidth={2}
                  activeDot={{
                    r: 8,
                    cursor: "pointer",
                    strokeWidth: 3,
                    fill: `hsl(${(index * 137.5) % 360}, 70%, 50%)`,
                    onClick: (event: any, payload: any) => {
                      if (event && event.stopPropagation) event.stopPropagation();
                      if (payload && payload.payload) {
                        const data = payload.payload;
                        const responseKey = `${test}_response`;
                        const response = data[responseKey];

                        if (response) {
                          setViewingReviewNote({ response });
                        }
                      }
                    },
                  }}
                  dot={{
                    r: 6,
                    cursor: "pointer",
                    strokeWidth: 2,
                    fill: `hsl(${(index * 137.5) % 360}, 70%, 50%)`,
                    onClick: (event: any, payload: any) => {
                      if (event && event.stopPropagation) event.stopPropagation();
                      if (payload && payload.payload) {
                        const data = payload.payload;
                        const responseKey = `${test}_response`;
                        const response = data[responseKey];

                        if (response) {
                          setViewingReviewNote({ response });
                        }
                      }
                    },
                  }}
                  name={formatTestName(test)}
                  isAnimationActive={false}
                />
              </Fragment>
            ),
          )}
        </ComposedChart>
      </ResponsiveContainer>
    );
  }, [memoizedChartData, activeTab, memoizedSelectedTests, CustomTooltip]);

  if (loading || roleCheckLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">
          {roleCheckLoading
            ? "Checking user permissions..."
            : "Loading patient data..."}
        </div>
      </div>
    );
  }

  if (error || !patient) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="text-lg text-red-500">
          {error || "Patient not found"}
        </div>
        <div className="flex space-x-4">
          <Button onClick={() => navigate(-1)}>Back to Patients</Button>
          <Button
            onClick={generateAuditReport}
            disabled={auditReportLoading}
            className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
          >
            <FileText className="w-4 h-4" />
            {auditReportLoading ? "Generating..." : "Patient Logs Report"}
          </Button>
        </div>
      </div>
    );
  }

  const hipaaConsent = getHipaaConsent();
  const emailConsent = getEmailConsent();
  const smsConsent = getSmsConsent();

  const dob = form.watch("date_of_birth");
  let patientAge: number | null = null;

  if (dob) {
    const dobDate = new Date(dob);
    if (!isNaN(dobDate.getTime())) {
      const diff = Date.now() - dobDate.getTime();
      patientAge = diff < 0 ? 0 : Math.abs(new Date(diff).getUTCFullYear() - 1970);
    }
  }

  const formSelectedTests = (form.watch("selected_tests") || []) as string[];

  const validTemplates = questionnaireTemplates.filter((template: any) => {
    if (patientAge === null) return true;
    const min = typeof template.min_age === 'number' && !isNaN(template.min_age) ? template.min_age :
      (typeof template.age_min === 'number' && !isNaN(template.age_min) ? template.age_min : 0);
    const max = typeof template.max_age === 'number' && !isNaN(template.max_age) ? template.max_age :
      (typeof template.age_max === 'number' && !isNaN(template.age_max) ? template.age_max : 999);

    // Show template if it matches age OR if it's already selected for this patient
    return (patientAge >= min && patientAge <= max) || formSelectedTests.includes(template.id);
  });

  return (
    <div className="space-y-8 min-h-screen bg-[#f7f8fa] -m-6 p-6 lg:p-10 font-sans">
      {/* Audit Report Modal */}
      {showAuditReport && patient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-2xl font-bold">Patient Logs Report</h2>
              <div className="flex space-x-2">
                <Button
                  onClick={handleOpenEmailReportModal}
                  disabled={emailSending}
                  variant="outline"
                  className="flex items-center gap-1.5 border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                >
                  <Mail className="w-4 h-4" />
                  {emailSending ? "Sending..." : "Email Report"}
                </Button>
                <Button
                  onClick={printAuditReport}
                  className="bg-[#1a7a4a] hover:bg-[#145d39] text-white flex items-center gap-1.5 shadow-sm"
                >
                  Download / Print PDF
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setShowAuditReport(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </Button>
              </div>
            </div>

            <div className="flex-1 overflow-auto p-6" id="audit-report-content">
              {/* Logo - Top Left */}
              <div
                className="logo-container"
                style={{ textAlign: "left", marginBottom: "20px" }}
              >
                <img
                  src="/MHAI_FULL_LOGO.png"
                  alt="Monitor Health AI Logo"
                  className="logo"
                  style={{ maxWidth: "200px", height: "auto" }}
                  onError={(e) => {
                    const img = e.target as HTMLImageElement;
                    img.style.display = "none";
                  }}
                />
              </div>

              {/* Report Header */}
              <div className="header text-center pt-8 pb-4 mb-8">
                <h1 className="text-3xl font-bold text-gray-800 mt-6 mb-3 tracking-tight">
                  PATIENT LOGS REPORT
                </h1>
                <p className="text-lg text-gray-600 mb-6">
                  Patient Healthcare Communication &amp; Consent Logs
                </p>
                <div className="pt-6 border-t border-gray-200/80 inline-block px-8">
                  <p className="text-sm text-gray-500 font-medium">
                    Generated on: {formatDate(new Date().toISOString())}
                  </p>
                  <p className="text-sm text-gray-500 mt-2 font-medium">
                    Hospital: {patient?.hospital_name || (patientEncounters.length > 0 ? getEncounterHospitalName(patientEncounters[0]) : "Ally Psychiatry")}
                  </p>
                </div>
              </div>

              {/* Patient Information Section */}
              <div className="section">
                <h2 className="section-title text-xl font-bold mb-4 border-b pb-2">
                  PATIENT IDENTIFICATION
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <div className="info-row">
                    <span className="label font-semibold">Full Name:</span>
                    <span className="value ml-2">
                      {patient.first_name}{patient.middle_name ? ` ${patient.middle_name}` : ''} {patient.last_name}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="label font-semibold">
                      Primary Provider Name:
                    </span>
                    <span className="value ml-2">
                      {getProviderName(patient)}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="label font-semibold">
                      Secondary Provider Name:
                    </span>
                    <span className="value ml-2">
                      {getSecondaryProviderName(patient)}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="label font-semibold">Date of Birth:</span>
                    <span className="value ml-2">
                      {formatDate(patient.date_of_birth, "MMMM d, yyyy")}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="label font-semibold">Mobile Number:</span>
                    <span className="value ml-2">{formatPhoneDisplay(patient.mobile_number)}</span>
                  </div>
                  <div className="info-row">
                    <span className="label font-semibold">Email:</span>
                    <span className="value ml-2">
                      {patient.email || "Not provided"}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="label font-semibold">
                      Registration Date:
                    </span>
                    <span className="value ml-2">
                      {formatDate(patient.created_at, "MMM d, yyyy · h:mm a")}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="label font-semibold">
                      Patient Acknowledgement Date:
                    </span>
                    <span className="value ml-2 text-emerald-800 font-semibold">
                      {getPatientAcknowledgementDate(patient)}
                    </span>
                  </div>
                  <div className="info-row col-span-2">
                    <span className="label font-semibold">
                      Location:
                    </span>
                    <span className="value ml-2 font-medium text-gray-800">
                      {getPatientLocationDisplay(patient)}
                    </span>
                  </div>
                </div>
              </div>


              {/* Consent Audit Trail */}
              <div className="section mt-8">
                <h2 className="section-title text-xl font-bold mb-4 border-b pb-2">
                  CONSENT AUDIT TRAIL
                </h2>
                {renderConsentHistory(patient.consent_logs, true)}
              </div>

              {/* Patient Encounters */}
              <div className="section mt-8">
                <h2 className="section-title text-xl font-bold mb-4 border-b pb-2">
                  PATIENT ENCOUNTERS HISTORY
                </h2>

                {patientEncounters.length > 0 ? (
                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                            Encounter Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                            Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                            Primary Provider
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                            Branch
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                            Hospital
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                            Encounter Start
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                            Review Window
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                            Chief Complaint
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {patientEncounters.map((encounter) => {
                          // Show call button for TeleHealth/telephonic scheduled appointments
                          const isTelephonic = encounter.encounter_type === "TeleHealth" || encounter.encounter_type === "RemoteTheropyMonitoring";
                          const isScheduled = encounter.status === "scheduled";
                          const appointmentTime = encounter.start_time ? new Date(encounter.start_time).getTime() : null;
                          const now = Date.now();
                          const canCallFromAppointment = isTelephonic && isScheduled && appointmentTime
                            ? now >= (appointmentTime - 10 * 60 * 1000) && now <= (appointmentTime + 20 * 60 * 1000)
                            : false;
                          return (
                            <tr
                              key={encounter.encounter_id}
                              className="hover:bg-gray-50"
                            >
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-r border-gray-100">
                                {formatDate(encounter.encounter_date, "MMM d, yyyy · h:mm a")}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm border-r border-gray-100">
                                {encounter.encounter_type === "InPerson" ? (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border border-emerald-200 bg-emerald-50 text-emerald-700">
                                    <Building2 className="h-3 w-3" />
                                    In-Person Visit
                                  </span>
                                ) : encounter.encounter_type === "TeleHealth" ? (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border border-indigo-200 bg-indigo-50 text-indigo-700">
                                    <Video className="h-3 w-3" />
                                    Telehealth (Virtual)
                                  </span>
                                ) : encounter.encounter_type === "RemoteTheropyMonitoring" ? (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border border-purple-200 bg-purple-50 text-purple-700">
                                    Behavioral Health
                                  </span>
                                ) : (
                                  <span className="text-gray-500 font-medium">
                                    {encounter.encounter_type === "SMS_Survey" ? "SMS Survey" : encounter.encounter_type}
                                  </span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm border-r border-gray-100">
                                <span
                                  className={`px-2 py-1 rounded text-xs font-medium ${encounter.status === "active"
                                    ? "bg-green-100 text-green-800"
                                    : encounter.status === "completed"
                                      ? "bg-blue-100 text-blue-800"
                                      : "bg-gray-100 text-gray-800"
                                    }`}
                                >
                                  {encounter.status.charAt(0).toUpperCase() +
                                    encounter.status.slice(1)}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500 border-r border-gray-100">
                                {getProviderName(encounter)}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-800 border-r border-gray-100 font-medium">
                                {getEncounterBranchName(encounter)}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-600 border-r border-gray-100">
                                {getEncounterHospitalName(encounter)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600 border-r border-gray-100">
                                {encounter.start_time
                                  ? formatDate(encounter.start_time, "MMM d, yyyy · h:mm a")
                                  : encounter.encounter_date
                                    ? formatDate(encounter.encounter_date, "MMM d, yyyy")
                                    : "—"}
                              </td>
                              <td className="px-6 py-4 text-xs border-r border-gray-100">
                                {encounter.clinical_review_started_at || encounter.clinical_review_ended_at ? (
                                  <div className="space-y-1">
                                    <div className="text-emerald-700 font-semibold flex items-center gap-1">
                                      <span>Started:</span>
                                      <span className="font-mono text-[11px]">{formatDate(encounter.clinical_review_started_at || "", "h:mm:ss a")}</span>
                                    </div>
                                    <div className="text-slate-600 flex items-center gap-1">
                                      <span>Ended:</span>
                                      <span className="font-mono text-[11px]">{formatDate(encounter.clinical_review_ended_at || "", "h:mm:ss a")}</span>
                                    </div>
                                    {encounter.clinical_review_duration ? (
                                      <span className="inline-block bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded-full mt-0.5">
                                        ⏱ {Math.floor(encounter.clinical_review_duration / 60)} min{encounter.clinical_review_duration % 60 > 0 ? ` ${encounter.clinical_review_duration % 60}s` : ""} review
                                      </span>
                                    ) : null}
                                  </div>
                                ) : (
                                  <span className="text-gray-400 italic text-xs">Pending review</span>
                                )}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500 border-r border-gray-100">
                                {encounter.chief_complaint ||
                                  "No complaint recorded"}
                              </td>
                              <td className="px-6 py-4 text-sm">
                                {isTelephonic && isScheduled && (
                                  canCallFromAppointment ? (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      disabled={callNowLoading}
                                      onClick={handleCallNow}
                                      className="flex items-center gap-1 border-green-200 text-green-700 hover:bg-green-50"
                                    >
                                      <PhoneCall className="h-3 w-3" />
                                      {callNowLoading ? "Calling..." : "Call Now"}
                                    </Button>
                                  ) : (
                                    <span className="text-xs text-gray-400">
                                      {appointmentTime && now < (appointmentTime - 10 * 60 * 1000)
                                        ? `Call opens at ${new Date(appointmentTime - 10 * 60 * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                                        : "Call window passed"}
                                    </span>
                                  )
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                    <p className="text-gray-500 text-center">
                      No patient encounters have been recorded.
                    </p>
                  </div>
                )}
              </div>

              {/* SMS Communication Logs */}
              <div className="section mt-8">
                <h2 className="section-title text-xl font-bold mb-4 border-b pb-2">
                  SMS COMMUNICATION LOGS
                </h2>

                {smsLogs.length > 0 ? (
                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase border-b">
                            Date &amp; Time
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase border-b">
                            Communication Type
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase border-b">
                            Recipient
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase border-b">
                            Outreach Event
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase border-b">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {smsLogs
                          .sort((a, b) => new Date(b.updated_at || b.created_at || 0).getTime() - new Date(a.updated_at || a.created_at || 0).getTime())
                          .map((log) => {
                            const isQuestionnaire = /questionnaire/i.test(log.message_body || "");
                            const isAppointment = /appointment/i.test(log.message_body || "");
                            const eventType = isQuestionnaire
                              ? "Questionnaire Outreach Link Dispatched"
                              : isAppointment
                                ? "Appointment Outreach Notification"
                                : "Clinical Outreach SMS";
                            const timestamp = log.updated_at || log.created_at || (log as any).sent_at;

                            return (
                              <tr key={log.id}>
                                <td className="px-4 py-3 text-sm text-gray-900 border-r whitespace-nowrap">
                                  {timestamp ? formatDate(timestamp, "MMM d, yyyy · h:mm a") : "—"}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900 font-medium border-r whitespace-nowrap">
                                  Outbound SMS
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-700 border-r font-mono">
                                  {patient?.mobile_number || "Patient Mobile"}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-800 border-r">
                                  <span className="font-medium text-gray-900">{eventType}</span>
                                  {log.unique_page_id && (
                                    <span className="block text-xs text-gray-500 font-mono mt-0.5">
                                      Ref ID: {log.unique_page_id}
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm">
                                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wider ${
                                    log.status?.toLowerCase() === "sent" || log.status?.toLowerCase() === "delivered"
                                      ? "bg-green-100 text-green-800 border border-green-200"
                                      : "bg-orange-100 text-orange-800 border border-orange-200"
                                  }`}>
                                    {log.status || "Sent"}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                    <p className="text-gray-500 text-center">
                      No SMS communications have been recorded for this patient.
                    </p>
                  </div>
                )}
              </div>

              {/* Call Communication Logs */}
              <div className="section mt-8">
                <h2 className="section-title text-xl font-bold mb-4 border-b pb-2">
                  CALL COMMUNICATION LOGS
                </h2>

                {allCallLogs.length > 0 ? (
                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase border-b">
                            Date
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase border-b">
                            Start Time
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase border-b">
                            End Time
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase border-b">
                            Duration
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase border-b">
                            Call Type
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase border-b">
                            Status
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase border-b">
                            Transcript / Discussion Notes
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {allCallLogs
                          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                          .map((log, index) => {
                            const callTimes = getCallTimes(log);
                            return (
                              <tr key={index}>
                                <td className="px-4 py-3 text-sm text-gray-900 border-r whitespace-nowrap">
                                  {formatDate(log.created_at)}
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-800 font-mono border-r whitespace-nowrap">
                                  {callTimes.startTime}
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-800 font-mono border-r whitespace-nowrap">
                                  {callTimes.isInProgress ? (
                                    <span className="inline-flex items-center gap-1 text-amber-600 font-medium font-sans">
                                      <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                                      In Progress
                                    </span>
                                  ) : (
                                    callTimes.endTime
                                  )}
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-700 font-mono border-r whitespace-nowrap">
                                  {callTimes.isInProgress ? (
                                    <span className="text-amber-600 font-medium font-sans">Live</span>
                                  ) : (
                                    callTimes.duration
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900 font-medium border-r capitalize whitespace-nowrap">
                                  {log.call_type}
                                </td>
                                <td className="px-4 py-3 text-sm border-r whitespace-nowrap">
                                  <span
                                    className={`px-2 py-1 rounded text-xs font-medium inline-flex items-center gap-1 ${
                                      log.status === 'completed'
                                        ? 'bg-green-100 text-green-800 border border-green-200'
                                        : log.status === 'in_progress'
                                        ? 'bg-amber-100 text-amber-800 border border-amber-300'
                                        : 'bg-orange-100 text-orange-800 border border-orange-200'
                                    }`}
                                    title={log.status === 'in_progress' ? 'Amazon Connect outbound call initiated and currently ongoing' : undefined}
                                  >
                                    {log.status === 'in_progress' && (
                                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-ping mr-0.5" />
                                    )}
                                    {log.status}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-700 whitespace-pre-wrap">
                                  {log.discussion_notes || log.notes || "—"}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-4 bg-gray-50 rounded-lg text-gray-500 text-center italic border border-dashed">
                    No call records found for this period.
                  </div>
                )}
              </div>

              {/* Questionnaire Responses Summary */}
              <div className="section mt-8">
                <h2 className="section-title text-xl font-bold mb-4 border-b pb-2">
                  QUESTIONNAIRE RESPONSES SUMMARY
                </h2>

                {questionnaireResponses.length > 0 ? (
                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Test Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Score
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Self Reported
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Review Time
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {questionnaireResponses.map((response) => (
                          <tr
                            key={response.id}
                            className="bg-white hover:bg-gray-50"
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {formatDate(response.created_at)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {response.questionnaire_type}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                              {response.total_score}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {response.self_reported ? "Yes" : "No"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <span
                                className={`px-2 py-1 rounded text-xs font-medium ${response.status === "completed"
                                  ? "bg-green-100 text-green-800"
                                  : response.status === "in_progress"
                                    ? "bg-blue-100 text-blue-800"
                                    : response.status === "pending_review"
                                      ? "bg-yellow-100 text-yellow-800"
                                      : response.status === "rejected"
                                        ? "bg-red-100 text-red-800"
                                        : "bg-gray-100 text-gray-800"
                                  }`}
                              >
                                {response.status
                                  .split("_")
                                  .map(
                                    (word) =>
                                      word.charAt(0).toUpperCase() +
                                      word.slice(1),
                                  )
                                  .join(" ")}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {response.time_spent_on_review
                                ? `${response.time_spent_on_review} mins`
                                : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                    <p className="text-gray-500 text-center">
                      No questionnaire responses have been recorded.
                    </p>
                  </div>
                )}
              </div>


              {/* Report Footer */}
              <div className="section border-t pt-6 mt-8">
                <div className="text-center text-sm text-gray-600">
                  <p className="font-semibold">REPORT AUTHENTICATION</p>
                  <p>
                    This report was generated digitally by MonitorHealth.AI to present patient’s acknowledgement & consents to participate in Behavioral Health Integration Services in-between visits by AllyPsychiatry Care Team
                  </p>
                  <p className="mt-3">Generated on: {formatDate(new Date().toISOString())}</p>
                  <p className="mt-4 font-semibold">
                    This document contains confidential patient information and
                    is intended for authorized use only.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Report Recipient Modal */}
      {isEmailReportModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 z-[70] animate-in fade-in duration-150">
          <div className="bg-white rounded-2xl w-full max-w-xl shadow-2xl border border-gray-100 overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-5 bg-gradient-to-r from-emerald-800 to-emerald-700 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center backdrop-blur-sm">
                  <Mail className="w-5 h-5 text-emerald-100" />
                </div>
                <div>
                  <h3 className="text-lg font-bold leading-tight">Send Patient Logs Report</h3>
                  <p className="text-xs text-emerald-100 mt-0.5">
                    Select care providers or additional recipients to email the report
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => !emailSending && setIsEmailReportModalOpen(false)}
                disabled={emailSending}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-50 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                Recipients Selection
              </p>

              {/* Primary Provider */}
              <div
                className={`p-4 rounded-xl border transition-all ${
                  emailModalPrimarySelected
                    ? "border-emerald-300 bg-emerald-50/50 shadow-xs"
                    : "border-gray-200 bg-gray-50/50 hover:bg-gray-50"
                }`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="emailModalPrimaryCheckbox"
                    checked={emailModalPrimarySelected}
                    onChange={(e) => setEmailModalPrimarySelected(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                  />
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center justify-between">
                      <label
                        htmlFor="emailModalPrimaryCheckbox"
                        className="text-sm font-semibold text-gray-900 cursor-pointer"
                      >
                        {emailModalPrimaryName || "Primary Provider"}
                      </label>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 uppercase tracking-wider">
                        Primary Provider
                      </span>
                    </div>
                    {emailModalPrimarySelected && (
                      <div>
                        <Input
                          type="email"
                          placeholder="primary.provider@hospital.org"
                          value={emailModalPrimaryEmail}
                          onChange={(e) => setEmailModalPrimaryEmail(e.target.value)}
                          className="h-9 text-sm bg-white border-emerald-200 focus:border-emerald-500 focus:ring-emerald-500"
                        />
                        {!emailModalPrimaryEmail.trim() && (
                          <p className="text-[11px] text-amber-600 font-medium mt-1">
                            ⚠️ No email on file. Please enter recipient email.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Secondary Provider (if assigned) */}
              {emailModalHasSecondary && (
                <div
                  className={`p-4 rounded-xl border transition-all ${
                    emailModalSecondarySelected
                      ? "border-emerald-300 bg-emerald-50/50 shadow-xs"
                      : "border-gray-200 bg-gray-50/50 hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="emailModalSecondaryCheckbox"
                      checked={emailModalSecondarySelected}
                      onChange={(e) => setEmailModalSecondarySelected(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                    />
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center justify-between">
                        <label
                          htmlFor="emailModalSecondaryCheckbox"
                          className="text-sm font-semibold text-gray-900 cursor-pointer"
                        >
                          {emailModalSecondaryName || "Secondary Provider"}
                        </label>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 uppercase tracking-wider">
                          Secondary Provider
                        </span>
                      </div>
                      {emailModalSecondarySelected && (
                        <div>
                          <Input
                            type="email"
                            placeholder="secondary.provider@hospital.org"
                            value={emailModalSecondaryEmail}
                            onChange={(e) => setEmailModalSecondaryEmail(e.target.value)}
                            className="h-9 text-sm bg-white border-emerald-200 focus:border-emerald-500 focus:ring-emerald-500"
                          />
                          {!emailModalSecondaryEmail.trim() && (
                            <p className="text-[11px] text-amber-600 font-medium mt-1">
                              ⚠️ No email on file. Please enter recipient email.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Additional / Custom Recipient */}
              <div
                className={`p-4 rounded-xl border transition-all ${
                  emailModalCustomSelected
                    ? "border-emerald-300 bg-emerald-50/50 shadow-xs"
                    : "border-gray-200 bg-gray-50/50 hover:bg-gray-50"
                }`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="emailModalCustomCheckbox"
                    checked={emailModalCustomSelected}
                    onChange={(e) => setEmailModalCustomSelected(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                  />
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center justify-between">
                      <label
                        htmlFor="emailModalCustomCheckbox"
                        className="text-sm font-semibold text-gray-900 cursor-pointer"
                      >
                        Additional Recipient
                      </label>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 uppercase tracking-wider">
                        Custom
                      </span>
                    </div>

                    {emailModalCustomSelected && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                        <div>
                          <Label className="text-xs text-gray-600 mb-1 block">Recipient Name / Role</Label>
                          <Input
                            type="text"
                            placeholder="e.g. Care Coordinator"
                            value={emailModalCustomName}
                            onChange={(e) => setEmailModalCustomName(e.target.value)}
                            className="h-9 text-sm bg-white"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-600 mb-1 block">Recipient Email</Label>
                          <Input
                            type="email"
                            placeholder="recipient@example.com"
                            value={emailModalCustomEmail}
                            onChange={(e) => setEmailModalCustomEmail(e.target.value)}
                            className="h-9 text-sm bg-white border-emerald-200 focus:border-emerald-500 focus:ring-emerald-500"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="rounded-lg bg-slate-50 p-3 border border-slate-200 text-xs text-slate-600 flex items-start gap-2">
                <span className="font-bold text-slate-800 shrink-0">Note:</span>
                <span>
                  Each selected recipient will receive a personalized registration &amp; Behavioral Health Integration Services consent email containing direct access to download the official PDF report.
                </span>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEmailReportModalOpen(false)}
                disabled={emailSending}
                className="text-gray-700 border-gray-300 hover:bg-gray-100"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSendEmailReport}
                disabled={emailSending || (!emailModalPrimarySelected && !emailModalSecondarySelected && !emailModalCustomSelected)}
                className="bg-emerald-700 hover:bg-emerald-800 text-white flex items-center gap-2 shadow-sm font-semibold"
              >
                {emailSending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending Email...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4" />
                    Send Report
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header with Time Selection */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-10">
        <div>
          <h1 className="text-3xl lg:text-4xl font-extrabold text-[#1a7a4a] tracking-tight">
            Patient Details
          </h1>
          <div className="flex items-center gap-3 mt-3">
            <div className="inline-flex items-center rounded-xl bg-[#1a7a4a]/10 text-[#1a7a4a] px-3 py-1 text-xs font-bold uppercase tracking-wider">
              {userRole ? formatRoleName(userRole) : "Role Unknown"}
            </div>
            {canAddNotes ? (
              <div className="flex items-center gap-1 text-xs font-medium text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-100">
                <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                Edit Access
              </div>
            ) : (
              <div className="flex items-center gap-1 text-xs font-medium text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-100">
                Read Only
              </div>
            )}

            {/* MindWise Weekly Ingestion Indicator */}
            <div 
              onClick={() => setIsMindWiseModalOpen(true)}
              className="inline-flex items-center gap-2 text-xs font-semibold bg-gradient-to-r from-blue-50 via-indigo-50 to-emerald-50 text-blue-900 px-3 py-1 rounded-full border border-blue-200 shadow-2xs cursor-pointer hover:border-blue-400 hover:shadow-xs transition-all"
              title="Click to view MindWise Ingestion & Sync Details"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              <span>MindWise Ingested (This Week)</span>
              <span className="text-[10px] bg-blue-200/80 text-blue-950 px-2 py-0.5 rounded font-mono font-bold">
                {patient.mindwise_uuid || "MW-PT-9042"}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-gray-500 hover:text-[#1a7a4a] hover:bg-white rounded-xl transition-all"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Patients
          </Button>

          {canAddNotes && (
            <div className="flex items-center gap-2.5 px-4 py-2 bg-white rounded-2xl border border-gray-100 shadow-sm">
              <Label
                htmlFor="time-spent"
                className="text-sm font-semibold text-gray-500 whitespace-nowrap"
              >
                Time on Page:
              </Label>
              <select
                id="time-spent"
                value={selectedTimeSpent === null ? "" : selectedTimeSpent}
                onChange={(e) => {
                  const val = e.target.value ? Number(e.target.value) : null;
                  setSelectedTimeSpent(val);
                  if (val !== null) {
                    saveClinicalReviewTime(val);
                  }
                }}
                disabled={savingEncounterReview}
                className="bg-transparent border-none focus:ring-0 text-sm font-bold text-gray-900 cursor-pointer"
              >
                <option value="">Select a time</option>
                {timeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {savingEncounterReview && (
                <Loader2 className="w-3.5 h-3.5 animate-spin text-[#1a7a4a]" />
              )}
              {encounterReviewSaved && (
                <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                  ✓ Saved to Encounter
                </span>
              )}
            </div>
          )}

          <Button
            onClick={() => handleMindWiseSync()}
            disabled={mindwiseSyncing}
            variant="outline"
            className="border-blue-200 text-blue-700 hover:bg-blue-50 bg-white rounded-2xl h-11 px-5 shadow-sm transition-all hover:scale-[1.02] flex items-center gap-2 font-semibold"
            title="Sync PHQ-9 & GAD-7 assessments and clinician outreach note to MindWise"
          >
            {mindwiseSyncing ? (
              <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
            ) : (
              <RefreshCw className="w-4 h-4 text-blue-600" />
            )}
            {mindwiseSyncing ? "Syncing MindWise..." : "Sync MindWise"}
          </Button>

          <Button
            onClick={generateAuditReport}
            disabled={auditReportLoading}
            className="bg-[#1a7a4a] hover:bg-[#15633c] text-white rounded-2xl h-11 px-6 shadow-lg shadow-green-100 transition-all hover:scale-[1.02] flex items-center gap-2 font-semibold"
          >
            <FileText className="w-4 h-4" />
            {auditReportLoading ? "Generating..." : "Patient Logs"}
          </Button>
        </div>
      </div>

      <div className={`grid gap-6 ${selectedTests.length > 0 ? "md:grid-cols-3" : "grid-cols-1"}`}>
        {/* Patient Info Card */}
        <Card className={selectedTests.length > 0 ? "h-full" : "w-full overflow-hidden"}>
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between">
              <div className="flex flex-col items-center space-y-3 w-full sm:flex-row sm:space-y-0 sm:space-x-4 sm:items-start">
                <div className="relative group">
                  <Avatar className="h-20 w-20 border-2 border-white shadow-md ring-2 ring-gray-50 transition-all group-hover:ring-green-100">
                    <AvatarImage src={patient.photo_url || undefined} className="object-cover" />
                    <AvatarFallback className="bg-gradient-to-br from-[#1a7a4a] to-emerald-600 text-white text-xl font-bold">
                      {getInitials(patient.first_name, patient.last_name)}
                    </AvatarFallback>
                  </Avatar>
                  {patient.is_face_biometric_registered && (
                    <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-1.5 border-2 border-white shadow-sm" title="Face Registered">
                      <ShieldCheck className="h-3.5 w-3.5 text-white" />
                    </div>
                  )}
                </div>
                <div className="flex-1 text-center sm:text-left pt-1">
                  <h2 className="text-2xl font-bold text-gray-900 tracking-tight leading-tight mb-1">
                    {patient.first_name} {patient.middle_name ? patient.middle_name + " " : ""}{patient.last_name}
                  </h2>
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-3 gap-y-1 text-sm text-gray-500">
                    <span
                      onClick={() => handleCopyId(patient.id)}
                      className="flex items-center gap-1.5 font-mono text-[10px] bg-gray-50 px-2 py-0.5 rounded border border-gray-100 cursor-pointer hover:bg-gray-100 hover:border-gray-200 transition-all group/id"
                      title="Click to copy ID"
                    >
                      <User2 className="w-3 h-3 text-gray-400 group-hover/id:text-green-600 transition-colors" />
                      ID: {patient.id.slice(0, 8)}...
                      <Copy className="w-2.5 h-2.5 text-gray-300 opacity-0 group-hover/id:opacity-100 transition-all ml-0.5" />
                    </span>
                    {patient.social_security_number && (
                      <span className="flex items-center gap-1 font-mono text-[10px] bg-gray-50 px-2 py-0.5 rounded border border-gray-100">
                        <CreditCard className="w-3 h-3 text-gray-400" /> SSN: {patient.social_security_number}
                      </span>
                    )}
                    <span 
                      onClick={() => setIsMindWiseModalOpen(true)}
                      className="inline-flex items-center gap-1.5 font-mono text-[10px] bg-blue-50 text-blue-800 px-2 py-0.5 rounded border border-blue-200 cursor-pointer hover:bg-blue-100 transition-colors"
                      title="MindWise Ingested Patient (Click to view sync details)"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      MindWise Ingestion: <strong className="font-semibold">{patient.mindwise_uuid || "MW-PT-9042"}</strong>
                    </span>
                  </div>
                </div>
              </div>
              <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleOpenEditModal}
                    className="rounded-full px-4 border-gray-200 hover:border-[#1a7a4a] hover:bg-green-50 transition-all text-xs font-semibold h-8"
                  >
                    <Edit className="h-3 w-3 mr-1.5" /> Edit
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <User className="h-5 w-5" />
                      Edit Patient Information
                    </DialogTitle>
                  </DialogHeader>
                  <Form {...form}>
                    <form
                      onSubmit={form.handleSubmit(handleSaveChanges)}
                      className="space-y-4"
                    >
                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="first_name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>First Name</FormLabel>
                              <FormControl>
                                <Input placeholder="First name" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="last_name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Last Name</FormLabel>
                              <FormControl>
                                <Input placeholder="Last name" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="middle_name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Middle Name</FormLabel>
                              <FormControl>
                                <Input placeholder="Middle name" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="social_security_number"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>SSN (Optional)</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="123-45-6789"
                                  maxLength={11}
                                  {...field}
                                  onInput={(e) => {
                                    const target = e.target as HTMLInputElement;
                                    let val = target.value.replace(/\D/g, "");
                                    if (val.length > 9) val = val.slice(0, 9);
                                    let formatted = "";
                                    if (val.length > 0) {
                                      formatted = val.slice(0, 3);
                                      if (val.length > 3) {
                                        formatted += "-" + val.slice(3, 5);
                                        if (val.length > 5) {
                                          formatted += "-" + val.slice(5, 9);
                                        }
                                      }
                                    }
                                    target.value = formatted;
                                    field.onChange(formatted);
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="date_of_birth"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Date of Birth</FormLabel>
                            <FormControl>
                              <Input type="date" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email (Optional)</FormLabel>
                            <FormControl>
                              <Input
                                type="email"
                                placeholder="Email"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="mobile_number"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Mobile Number</FormLabel>
                            <FormControl>
                              <div className="flex rounded-md border border-input overflow-hidden focus-within:ring-1 focus-within:ring-ring">
                                <span className="flex items-center px-3 bg-muted text-muted-foreground text-sm font-medium border-r border-input select-none">
                                  +1
                                </span>
                                <input
                                  className="flex-1 h-9 px-3 py-1 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
                                  placeholder="###-###-####"
                                  {...field}
                                  type="text"
                                  maxLength={12}
                                  onInput={(e) => {
                                    const target = e.target as HTMLInputElement;
                                    const digits = target.value.replace(/\D/g, "").slice(0, 10);
                                    // Auto-format as ###-###-####
                                    let formatted = digits;
                                    if (digits.length > 6) {
                                      formatted = digits.slice(0, 3) + "-" + digits.slice(3, 6) + "-" + digits.slice(6);
                                    } else if (digits.length > 3) {
                                      formatted = digits.slice(0, 3) + "-" + digits.slice(3);
                                    }
                                    target.value = formatted;
                                    field.onChange(formatted);
                                  }}
                                />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="grid grid-cols-1 gap-4">
                        <FormField
                          control={form.control}
                          name="primary_provider_id"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Primary Provider</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value || ""}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select Primary Provider" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {staffUsers
                                    .filter((staff) => staff.id && String(staff.id).trim() !== "")
                                    .map((staff) => (
                                      <SelectItem
                                        key={staff.id}
                                        value={String(staff.id)}
                                      >
                                        {staff.name || `${staff.first_name || ""} ${staff.last_name || ""}`.trim() || staff.id} {staff.role ? `(${staff.role})` : ""}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="secondary_provider_id"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Secondary Provider (Optional)</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value || ""}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select Secondary Provider" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="none">None</SelectItem>
                                  {staffUsers
                                    .filter((staff) => staff.id && String(staff.id).trim() !== "")
                                    .map((staff) => (
                                      <SelectItem
                                        key={staff.id}
                                        value={String(staff.id)}
                                      >
                                        {staff.name || `${staff.first_name || ""} ${staff.last_name || ""}`.trim() || staff.id} {staff.role ? `(${staff.role})` : ""}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/*Fetch the the boolean value of is_face_biometric_registered and make it deleteable ......just make it false and call the delete face api ....*/}

                      {/* Face Biometric Registration Status */}
                      <div className="col-span-2">
                        <FormLabel>Face Biometric Registration</FormLabel>
                        <div className="mt-2 p-4 border rounded-lg bg-gray-50">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div
                                className={`w-3 h-3 rounded-full ${patient?.is_face_biometric_registered ? "bg-green-500" : "bg-gray-400"}`}
                              ></div>
                              <span className="text-sm font-medium">
                                {patient?.is_face_biometric_registered
                                  ? "Registered"
                                  : "Not Registered"}
                              </span>
                            </div>
                            {!patient?.is_face_biometric_registered && (
                              <Button
                                type="button"
                                variant="default"
                                size="sm"
                                onClick={() => {
                                  setShowFaceRegistrationModal(true);
                                }}
                                className="text-xs bg-green-600 hover:bg-green-700"
                              >
                                <Camera className="w-3 h-3 mr-1" />
                                Register Face
                              </Button>
                            )}
                            {patient?.is_face_biometric_registered && (
                              <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                onClick={async () => {
                                  if (!patient) return;

                                  const currentUser = await getCurrentUser();
                                  const currentUserId = currentUser?.username;

                                  if (!currentUserId) {
                                    toast.error(
                                      "Authentication failed: No active session found",
                                    );
                                    return;
                                  }

                                  // Construct external image ID: uuid + "_" + first_name + "_" + last_name
                                  // const externalImageId = `${patient.id}_${patient.first_name}_${patient.last_name}`;
                                  const externalImageId = `${patient.id}_${clean(patient.first_name)}_${clean(patient.last_name)}`;

                                  console.log(
                                    "External Image ID:",
                                    externalImageId,
                                  );

                                  try {
                                    toast.loading(
                                      "Deleting face biometric data...",
                                      { id: "delete-face" },
                                    );

                                    const biometricService =
                                      biometricAuthService;
                                    // Don't pass second parameter - let it use the default admin UUID
                                    const result =
                                      await biometricService.deleteFace(
                                        externalImageId,
                                      );

                                    if (result.success) {
                                      try {
                                        await AuthService.updatePatient(patient.id, { is_face_biometric_registered: false });
                                      } catch (updateError) {
                                        console.error("Error updating patient in database:", updateError);
                                        toast.error(
                                          "Face deleted from biometric service but failed to update database",
                                          { id: "delete-face" },
                                        );
                                        return;
                                      }

                                      toast.success(
                                        "Face biometric data deleted successfully",
                                        { id: "delete-face" },
                                      );
                                      // Update local state
                                      setPatient((prev) =>
                                        prev
                                          ? {
                                            ...prev,
                                            is_face_biometric_registered: false,
                                          }
                                          : null,
                                      );
                                    } else {
                                      toast.error(
                                        result.error?.message ||
                                        "Failed to delete face biometric data",
                                        { id: "delete-face" },
                                      );

                                      // If it's an authentication error, suggest the biometric API might not be available
                                      if (result.error?.status === 401) {
                                        toast.info(
                                          "Note: Biometric API may not be available in development environment",
                                          { id: "delete-face-info" },
                                        );

                                        // For development: Allow manual override of biometric status
                                        if (
                                          confirm(
                                            "Biometric API is not available. Would you like to mark the biometric data as deleted for UI purposes only?",
                                          )
                                        ) {
                                          try {
                                            await AuthService.updatePatient(patient.id, { is_face_biometric_registered: false });
                                          } catch (updateError) {
                                            console.error("Error updating patient in database:", updateError);
                                            toast.error(
                                              "Failed to update database",
                                              { id: "delete-face" },
                                            );
                                            return;
                                          }

                                          setPatient((prev) =>
                                            prev
                                              ? {
                                                ...prev,
                                                is_face_biometric_registered: false,
                                              }
                                              : null,
                                          );
                                          toast.success(
                                            "Biometric status updated",
                                            { id: "delete-face" },
                                          );
                                        }
                                      }
                                    }
                                  } catch (error) {
                                    console.error(
                                      "Error deleting face biometric:",
                                      error,
                                    );
                                    toast.error(
                                      "Error deleting face biometric data. The biometric service may not be available.",
                                      { id: "delete-face" },
                                    );
                                  }
                                }}
                                className="text-xs"
                              >
                                Delete Face Data
                              </Button>
                            )}
                          </div>
                          {patient?.is_face_biometric_registered && (
                            <p className="text-xs text-gray-600 mt-2">
                              External Image ID: {patient.id}_
                              {patient.first_name}_{patient.last_name}
                            </p>
                          )}
                        </div>
                      </div>


                      {/* Test Selection */}
                      <div className="col-span-2">
                        <FormLabel>Enroll for MHAI (Assign Tests)</FormLabel>
                        <div className="mt-2 text-sm text-gray-600 mb-2">
                          Selected:{" "}
                          {(form.watch("selected_tests") || []).length}/2 tests
                          {(form.watch("selected_tests") || []).length >= 2 && (
                            <span className="ml-2 text-amber-600 text-xs">
                              (Maximum 2 tests allowed)
                            </span>
                          )}
                        </div>

                        {!isClinical && (
                          <div className="mb-3 text-sm text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
                            Only clinical staff can modify assigned tests.
                          </div>
                        )}

                        {templatesLoading ? (
                          <div className="flex items-center justify-center py-4">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                            <span className="ml-2">Loading tests...</span>
                          </div>
                        ) : validTemplates.length === 0 ? (
                          <div className="text-center py-4 text-red-500 col-span-2">
                            No tests available for this patient's age.
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {validTemplates.map((template) => {
                              const currentSelectedTests = form.watch("selected_tests") || [];
                              const isSelected = currentSelectedTests.includes(
                                template.id,
                              );
                              const isMaxSelected = !isSelected && currentSelectedTests.length >= 2;
                              const isDisabled = !isClinical || isMaxSelected;

                              return (
                                <div
                                  key={template.id}
                                  className={`flex items-center p-3 rounded-lg border transition-colors ${isSelected
                                    ? "bg-blue-50 border-blue-200"
                                    : "bg-white border-gray-200"
                                    } ${isDisabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:bg-gray-50"}`}
                                  onClick={() =>
                                    !isDisabled &&
                                    handleTestSelection(template.id, isSelected)
                                  }
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    disabled={isDisabled}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handleTestSelection(
                                        template.id,
                                        isSelected,
                                      );
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50"
                                  />
                                  <div className="ml-3 flex-1 flex flex-col">
                                    <label className={`text-sm font-medium text-gray-700 ${isDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}>
                                      {template.name}
                                    </label>
                                    {template.source_citations && (
                                      <span className="text-[11px] text-gray-500 mt-0.5 italic">
                                        Citation: {typeof template.source_citations === 'string' ? template.source_citations : JSON.stringify(template.source_citations)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {form.formState.errors.selected_tests && (
                          <p className="mt-1 text-sm text-red-600">
                            {form.formState.errors.selected_tests.message}
                          </p>
                        )}
                      </div>

                      <div className="flex justify-end gap-3 pt-4">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setIsEditModalOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          disabled={!form.formState.isDirty}
                        >
                          <Save className="h-4 w-4 mr-1" /> Save Changes
                        </Button>
                      </div>
                    </form>
                  </Form>
                </DialogContent>
              </Dialog>

              {/* Face Registration Modal */}
              <Dialog
                open={showFaceRegistrationModal}
                onOpenChange={handleCloseFaceRegistrationDialog}
              >
                <DialogContent className="max-w-3xl max-h-[95vh] overflow-y-auto rounded-3xl p-6 space-y-6">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-bold text-green-800">
                      {recordingPhase === "initial"
                        ? "Initial Face Scan"
                        : "Recording Additional Face Details"}
                    </DialogTitle>
                    <DialogDescription className="text-gray-600">
                      {recordingPhase === "initial"
                        ? "Please look directly at the camera for 3 seconds"
                        : "Please move your head slowly in different angles for additional face details"}
                    </DialogDescription>
                  </DialogHeader>

                  {statusMessage && (
                    <div className="mb-4 text-green-700 bg-green-50 px-4 py-3 rounded-xl border border-green-200">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        {statusMessage}
                      </div>
                    </div>
                  )}

                  {/* Camera Container */}
                  <div
                    className="relative bg-gray-900 rounded-2xl overflow-hidden"
                    style={{ minHeight: "400px" }}
                  >
                    <div
                      className="relative w-full"
                      style={{ paddingBottom: "75%" }}
                    >
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className="absolute top-0 left-0 w-full h-full object-cover"
                        style={{
                          transform: "scaleX(-1)",
                          minHeight: "400px",
                          maxHeight: "600px",
                        }}
                      />
                    </div>

                    {/* Recording Timer */}
                    <div className="absolute top-4 left-4 bg-black/90 text-white px-4 py-3 rounded-full text-base font-bold flex items-center gap-3 min-w-[80px] shadow-lg border-2 border-white/20">
                      <div
                        className={`w-3 h-3 rounded-full ${isRecording ? "bg-red-500 animate-pulse shadow-lg shadow-red-500/50" : "bg-gray-400"}`}
                      ></div>
                      <span className="text-white font-mono">{recordingTime}s</span>
                    </div>

                    {/* Recording Phase Indicator */}
                    <div className="absolute top-4 right-4 bg-black/70 text-white px-3 py-2 rounded-full text-xs font-medium">
                      {recordingPhase === "initial"
                        ? "Face Scan"
                        : "Recording Additional Face Details"}
                    </div>

                    {/* Control Buttons */}
                    <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-4">
                      {isRecording ? (
                        <Button
                          onClick={stopVideoRecording}
                          className="bg-red-500 hover:bg-red-600 text-white h-14 px-8 rounded-xl shadow-lg font-semibold transition-all hover:scale-105"
                        >
                          <div className="w-3 h-3 bg-white rounded-sm mr-3"></div>
                          Stop Recording
                        </Button>
                      ) : (
                        <Button
                          onClick={() => startVideoRecording(recordingPhase)}
                          disabled={recordingDisabled}
                          className="bg-green-500 hover:bg-green-600 text-white h-14 px-8 rounded-xl shadow-lg font-semibold transition-all hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
                        >
                          <div className="w-3 h-3 bg-white rounded-full mr-3 animate-pulse"></div>
                          {recordingPhase === "initial"
                            ? "Start Face Scan"
                            : "Start Recording Additional Face Details"}
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-gray-50 rounded-xl">
                    <p className="font-medium text-gray-800 mb-3">
                      Instructions:
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {recordingPhase === "initial" ? (
                        <>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                            Look directly at the camera
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                            Ensure good lighting
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                            Remove any accessories
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                            Keep a neutral expression
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                            Slowly turn your head left and right
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                            Tilt your head up and down
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                            Keep your face in frame
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                            Record for at least 10 seconds
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Face Found Dialog */}
              <Dialog
                open={showFaceFoundDialog}
                onOpenChange={setShowFaceFoundDialog}
              >
                <DialogContent className="max-w-md rounded-3xl">
                  <DialogHeader>
                    <DialogTitle className="text-xl font-bold text-red-600 flex items-center gap-2">
                      <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-red-600" />
                      </div>
                      Face Already Registered
                    </DialogTitle>
                  </DialogHeader>

                  <div className="py-4">
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                      <p className="text-red-800 font-medium mb-3">
                        We found a matching face in our system:
                      </p>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Name:</span>
                          <span className="font-medium">
                            {existingFaceData?.first_name}{" "}
                            {existingFaceData?.last_name}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">ID:</span>
                          <span className="font-mono text-xs">
                            {existingFaceData?.uuid}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Similarity:</span>
                          <span className="font-medium">
                            {existingFaceData?.confidence?.toFixed(2)}%
                          </span>
                        </div>
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm">
                      Please use a different image or <a
                        href="mailto:admin@monitorhealth.ai?subject=Support%20Request%20-%20Monitor%20Health%20AI&body=Hello%20Support%20Team,%0D%0A%0D%0AI%20need%20assistance%20with%20the%20following%20issue:%0D%0A"
                        className="text-[#1a7a4a] hover:underline"
                      >contact support</a> if this is an error.
                    </p>
                  </div>

                  <div className="flex justify-end">
                    <Button
                      onClick={() => setShowFaceFoundDialog(false)}
                      className="bg-green-500 hover:bg-green-600 text-white px-6 h-10 rounded-xl"
                    >
                      OK
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent className="pt-4 space-y-6">
            <div className="grid grid-cols-1 gap-5">
              {/* Primary Info Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50/50 p-3 rounded-2xl border border-transparent transition-all hover:border-gray-100 hover:bg-white hover:shadow-sm">
                  <div className="flex items-center gap-2 mb-1.5 text-gray-500">
                    <Cake className="w-3.5 h-3.5" />
                    <span className="text-[10px] font-bold uppercase tracking-wider">Date of Birth</span>
                  </div>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatDate(patient.date_of_birth, "MM-dd-yyyy")}
                  </p>
                </div>

                <div className="bg-gray-50/50 p-3 rounded-2xl border border-transparent transition-all hover:border-gray-100 hover:bg-white hover:shadow-sm">
                  <div className="flex items-center gap-2 mb-1.5 text-gray-500">
                    <User className="w-3.5 h-3.5" />
                    <span className="text-[10px] font-bold uppercase tracking-wider">Gender</span>
                  </div>
                  <p className="text-sm font-semibold text-gray-900 capitalize">
                    {patient.gender}
                  </p>
                </div>
              </div>

              {/* Contact and Membership */}
              <div className="space-y-3">
                <div className="bg-gray-50/50 p-3 rounded-2xl border border-transparent transition-all hover:border-gray-100 hover:bg-white hover:shadow-sm">
                  <div className="flex items-center gap-2 mb-2 text-gray-500">
                    <Phone className="w-3.5 h-3.5" />
                    <span className="text-[10px] font-bold uppercase tracking-wider">Contact Details</span>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      {formatPhoneDisplay(patient.mobile_number)}
                    </p>
                    {patient.email && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Mail className="w-3 h-3 opacity-60" />
                        <span className="truncate text-xs font-medium">{patient.email}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50/50 p-3 rounded-2xl border border-transparent transition-all hover:border-gray-100 hover:bg-white hover:shadow-sm">
                  <div className="flex items-center gap-2 mb-1.5 text-gray-500">
                    <Calendar className="w-3.5 h-3.5" />
                    <span className="text-[10px] font-bold uppercase tracking-wider">Member Since</span>
                  </div>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatDate(patient.created_at)}
                  </p>
                </div>
              </div>
            </div>

            {/* Consents Section */}
            <div className="pt-4 border-t border-gray-100">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="w-3.5 h-3.5 text-[#1a7a4a]" />
                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Consent Status</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className={`rounded-full px-3 py-1 text-[9px] font-bold border transition-all ${hipaaConsent.hasConsent ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"}`}>
                  HIPAA: {hipaaConsent.hasConsent ? "GIVEN" : "PENDING"}
                </Badge>
                <Badge variant="outline" className={`rounded-full px-3 py-1 text-[9px] font-bold border transition-all ${emailConsent.hasConsent ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-gray-50 text-gray-500 border-gray-200"}`}>
                  EMAIL: {emailConsent.hasConsent ? "YES" : "NO"}
                </Badge>
                <Badge variant="outline" className={`rounded-full px-3 py-1 text-[9px] font-bold border transition-all ${smsConsent.hasConsent ? "bg-purple-50 text-purple-700 border-purple-200" : "bg-gray-50 text-gray-500 border-gray-200"}`}>
                  SMS: {smsConsent.hasConsent ? "YES" : "NO"}
                </Badge>
              </div>
            </div>

            {/* Selected Tests */}
            {patient.selected_tests && patient.selected_tests.length > 0 && (
              <div className="pt-4 border-t border-gray-100">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="w-3.5 h-3.5 text-blue-600" />
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Assigned Questionnaires</span>
                </div>
                <div className="flex flex-wrap gap-2.5">
                  {patient.selected_tests?.map((test, index) => (
                    <Badge
                      key={index}
                      variant="outline"
                      className="bg-white text-gray-700 border-gray-200 cursor-pointer hover:bg-[#1a7a4a] hover:text-white hover:border-[#1a7a4a] hover:shadow-md hover:shadow-green-100 transition-all rounded-xl px-3 py-2 text-[11px] font-bold flex items-center gap-2 group/chip active:scale-95"
                      onClick={() => setTestAttemptDialog({ isOpen: true, testId: test })}
                    >
                      <div className="w-5 h-5 rounded-full bg-gray-50 flex items-center justify-center group-hover/chip:bg-white/20 transition-colors">
                        <PlayCircle className="w-3 h-3 text-[#1a7a4a] group-hover/chip:text-white" />
                      </div>
                      {test}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Test Attempt Dialog */}
        <Dialog
          open={testAttemptDialog.isOpen}
          onOpenChange={(open) => !open && setTestAttemptDialog({ isOpen: false, testId: null })}
        >
          <DialogContent className="sm:max-w-[440px] p-0 overflow-hidden rounded-2xl border-none shadow-2xl">
            <div className="bg-[#1a7a4a] p-8 text-center relative overflow-hidden">
              {/* Decorative background element */}
              <div className="absolute -top-12 -right-12 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
              <div className="absolute -bottom-12 -left-12 w-32 h-32 bg-black/10 rounded-full blur-2xl" />

              <div className="relative z-10 flex flex-col items-center">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center mb-4 shadow-inner ring-1 ring-white/30">
                  <PlayCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white tracking-tight">Begin Assessment</h3>
                <p className="text-green-50/80 text-sm mt-1 max-w-[280px] mx-auto leading-relaxed">
                  Select how you would like to proceed with the screening for <span className="font-bold text-white underline underline-offset-4 decoration-green-300/50">{patient.first_name}</span>.
                </p>
              </div>
            </div>

            <div className="p-6 bg-white space-y-4">
              <div className="grid grid-cols-1 gap-3">
                {/* Option 1: Selected Test */}
                <button
                  onClick={() => {
                    const encounterId = (patientEncounters[0] as any)?.encounter_id || (patientEncounters[0] as any)?.id || patient.encounter_id;
                    if (encounterId) {
                      const url = `/patients/${patient.id}/${encounterId}/questionnaire/${testAttemptDialog.testId}`;
                      navigate(url);
                    } else {
                      toast.error("No active encounter found. Please create one first.");
                    }
                    setTestAttemptDialog({ isOpen: false, testId: null });
                  }}
                  className="group flex items-start gap-4 p-4 rounded-xl border border-gray-100 hover:border-[#1a7a4a] hover:bg-green-50/50 transition-all text-left active:scale-[0.98]"
                >
                  <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0 group-hover:bg-[#1a7a4a] transition-colors">
                    <FileText className="w-5 h-5 text-[#1a7a4a] group-hover:text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-gray-900 group-hover:text-[#1a7a4a] transition-colors">Attempt {testAttemptDialog.testId}</p>
                    <p className="text-xs text-gray-500 mt-0.5">Start only the specific assessment you selected.</p>
                  </div>
                  <div className="self-center">
                    <ArrowLeft className="w-4 h-4 text-gray-300 group-hover:text-[#1a7a4a] rotate-180 transition-colors" />
                  </div>
                </button>

                {/* Option 2: All Assigned Tests */}
                <button
                  onClick={() => {
                    const encounterId = (patientEncounters[0] as any)?.encounter_id || (patientEncounters[0] as any)?.id || patient.encounter_id;
                    if (encounterId) {
                      const url = `/patients/${patient.id}/${encounterId}/questionnaire/all`;
                      navigate(url);
                    } else {
                      toast.error("No active encounter found. Please create one first.");
                    }
                    setTestAttemptDialog({ isOpen: false, testId: null });
                  }}
                  className="group flex items-start gap-4 p-4 rounded-xl border border-gray-100 hover:border-blue-500 hover:bg-blue-50/50 transition-all text-left active:scale-[0.98]"
                >
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-500 transition-colors">
                    <div className="relative">
                      <FileText className="w-5 h-5 text-blue-600 group-hover:text-white" />
                      <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-white rounded-full flex items-center justify-center group-hover:bg-blue-500 transition-colors">
                        <div className="w-1.5 h-1.5 bg-blue-600 rounded-full group-hover:bg-white" />
                      </div>
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-gray-900 group-hover:text-blue-600 transition-colors">Attempt All Assigned</p>
                    <p className="text-xs text-gray-500 mt-0.5">Complete all currently assigned questionnaires in one session.</p>
                  </div>
                  <div className="self-center">
                    <ArrowLeft className="w-4 h-4 text-gray-300 group-hover:text-blue-600 rotate-180 transition-colors" />
                  </div>
                </button>
              </div>

              <div className="pt-2">
                <Button
                  variant="ghost"
                  className="w-full text-xs text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-xl py-1"
                  onClick={() => setTestAttemptDialog({ isOpen: false, testId: null })}
                >
                  Cancel and return
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Questionnaire Graph Card */}
        {selectedTests.length > 0 && (
          <Card className="md:col-span-2">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl">
                Questionnaire Scores Over Time
              </CardTitle>
              <CardDescription className="text-base">
                Track progress across different tests (Click on data points or
                tooltips to view review logs)
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-2">
              {!canSeeHighLevel ? (
                <div className="flex flex-col items-center justify-center h-64 space-y-4 bg-gray-50 rounded-xl border border-dashed">
                  <p className="text-gray-500 italic">High-level health data is restricted for your role.</p>
                </div>
              ) : questionnaireResponses.length > 0 ? (
                <Tabs
                  value={activeTab}
                  onValueChange={setActiveTab}
                  className="space-y-4"
                >
                  <TabsList className="inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground">
                    <TabsTrigger value="all" className="text-sm px-3 py-1.5">
                      All Tests
                    </TabsTrigger>
                    {testNames.map((test) => (
                      <TabsTrigger
                        key={test}
                        value={test}
                        className="text-sm px-3 py-1.5"
                      >
                        {formatTestName(test)}
                      </TabsTrigger>
                    ))}
                  </TabsList>

                  <div className="h-96 mb-4">{ChartComponent}</div>
                </Tabs>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 space-y-4">
                  <p className="text-gray-500">No questionnaire data available</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Read-Only Review Log Modal */}
      {viewingReviewNote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">
                Response Details -{" "}
                {formatTestName(viewingReviewNote.response.questionnaire_type)}
              </h3>
              <div className="flex space-x-2">
                {(isClinical || isDoctor || isBranchAdmin) && (
                  <>
                    {isLatestResponse(viewingReviewNote.response.id.toString()) && !currentCallLog && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate("/appointments", { state: { openAddModal: true, patientId: patient?.id } })}
                        className="flex items-center gap-2 border-orange-200 text-orange-700 hover:bg-orange-50"
                      >
                        <CalendarClock className="h-4 w-4" />
                        Schedule a Call
                      </Button>
                    )}
                    {isLatestResponse(viewingReviewNote.response.id.toString()) && isCallExpired() && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setCurrentCallLog(null);
                          setIsScheduleCallModalOpen(true);
                        }}
                        className="flex items-center gap-2 border-red-200 text-red-600 hover:bg-red-50"
                      >
                        <CalendarClock className="h-4 w-4" />
                        Reschedule Call
                      </Button>
                    )}
                    {getCallNowStatus().canCall && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={callNowLoading}
                        onClick={handleCallNow}
                        className="flex items-center gap-2 border-green-200 text-green-700 hover:bg-green-50"
                      >
                        <PhoneCall className="h-4 w-4" />
                        {callNowLoading ? "Calling..." : "Call Patient Now"}
                      </Button>
                    )}
                  </>
                )}
                {canEditreviewNote(viewingReviewNote.response) && (
                  <Button
                    size="sm"
                    onClick={() => {
                      setEditingResponse(viewingReviewNote.response);
                      setViewingReviewNote(null);
                    }}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Add Review Note
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewingReviewNote(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </Button>
              </div>
            </div>

            <div className="flex-1 overflow-auto space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-600">
                      Test Date
                    </Label>
                    <p className="text-lg font-semibold">
                      {formatDate(viewingReviewNote.response.created_at)}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-600">
                      Test Score
                    </Label>
                    <p className="text-lg font-semibold text-blue-600">
                      {(() => {
                        try {
                          const qs = typeof viewingReviewNote.response.answers === 'string'
                            ? JSON.parse(viewingReviewNote.response.answers || "[]")
                            : (viewingReviewNote.response.answers || []);
                          const mx = calculateMaxScore(qs);
                          return mx !== null ? `${viewingReviewNote.response.total_score} / ${mx}` : viewingReviewNote.response.total_score;
                        } catch (e) {
                          return viewingReviewNote.response.total_score;
                        }
                      })()}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-600">
                      Test Type
                    </Label>
                    <p className="text-lg font-semibold">
                      {formatTestName(
                        viewingReviewNote.response.questionnaire_type,
                      )}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-600">
                      Self Reported
                    </Label>
                    <p className="text-lg font-semibold">
                      {viewingReviewNote.response.self_reported ? "Yes" : "No"}
                    </p>
                  </div>
                </div>
              </div>

              {(() => {
                try {
                  const questions = typeof viewingReviewNote.response.answers === 'string'
                    ? JSON.parse(viewingReviewNote.response.answers || "[]")
                    : (viewingReviewNote.response.answers || []);

                  if (canSeeDetail && Array.isArray(questions) && questions.length > 0) {
                    return (
                      <div className="mt-4 border-t pt-4">
                        <Label className="text-sm font-medium text-gray-600 mb-3 block">
                          Assessment Responses
                        </Label>
                        <div className="bg-blue-50/30 p-4 rounded-xl border border-blue-100/50 space-y-4">
                          {questions.map((q: any, i: number) => (
                            <div key={i} className="text-sm border-b border-blue-100/30 pb-3 last:border-0 last:pb-0">
                              <div className="flex gap-2">
                                <span className="font-semibold text-blue-900 min-w-[24px]">Q{i + 1}:</span>
                                <span className="text-gray-800 font-medium">{q.question || 'Assessment Item'}</span>
                              </div>
                              <div className="ml-8 mt-2 space-y-1">
                                <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                                  Result: {(q.selected_text || q.selected) ?? 'N/A'}
                                  {q.selected !== undefined && q.selected !== null ? ` (Score: ${q.selected})` : ''}
                                </div>
                                {q.comment && (
                                  <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded border border-gray-100 mt-1 italic">
                                    <span className="font-semibold not-italic">Note:</span> {q.comment}
                                  </div>
                                )}

                                {q.voice_url && canListenAudio && (
                                  <div className="mt-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => toggleAudio(q.voice_url)}
                                      className={`h-8 gap-2 ${playingAudio === q.voice_url ? 'border-blue-500 bg-blue-50 text-blue-700' : 'text-gray-600 border-gray-200'}`}
                                    >
                                      {playingAudio === q.voice_url ? (
                                        <>
                                          <PauseCircle className="w-4 h-4 animate-pulse" />
                                          Playing...
                                        </>
                                      ) : (
                                        <>
                                          <Mic className="w-3.5 h-3.5 text-blue-500" />
                                          <PlayCircle className="w-4 h-4" />
                                          Play Recording
                                        </>
                                      )}
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  if (!canSeeDetail) {
                    return (
                      <div className="mt-4 border-t pt-8 pb-4 text-center">
                        <p className="text-gray-500 italic">Detailed response data is restricted for your role.</p>
                      </div>
                    );
                  }
                  return null;
                } catch (e) {
                  return null;
                }
              })()}

              {/* Call History Section */}
              {fetchingCallLog ? (
                <div className="flex items-center justify-center py-4 text-gray-500 italic">
                  <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mr-2"></div>
                  Checking call history...
                </div>
              ) : currentCallLog && (
                <div className="mt-4 border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-sm font-medium text-gray-600">
                      Call History
                    </Label>
                    <Badge variant="outline" className={`${currentCallLog.status === 'completed' ? 'bg-green-50 text-green-700' : 'bg-orange-50 text-orange-700'}`}>
                      {currentCallLog.status === 'completed' ? 'Call Completed' : 'Call Pending/In-Progress'}
                    </Badge>
                  </div>

                  <div className="bg-blue-50/50 rounded-xl border border-blue-100 p-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Call Type:</span>
                        <span className="ml-2 font-medium capitalize">{currentCallLog.call_type}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Date:</span>
                        <span className="ml-2 font-medium">{formatDate(currentCallLog.created_at)}</span>
                      </div>
                    </div>

                    {currentCallLog.recording_s3_key && (
                      <div className="flex items-center gap-4 bg-white p-3 rounded-lg border border-blue-100">
                        <Phone className="h-5 w-5 text-blue-500" />
                        <div className="flex-1">
                          <p className="text-xs font-medium text-gray-500 mb-1">Call Recording</p>
                          <audio
                            controls
                            className="h-8 w-full"
                            src={currentCallLog.recording_presigned_url}
                          >
                            Your browser does not support the audio element.
                          </audio>
                        </div>
                      </div>
                    )}

                    {currentCallLog.discussion_notes ? (
                      <div className="bg-white p-3 rounded-lg border border-blue-100">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-medium text-gray-500">Transcript & Notes</p>
                          <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded uppercase">
                            Source: {currentCallLog.transcript_source || 'Unknown'}
                          </span>
                        </div>
                        <div className="text-sm text-gray-800 whitespace-pre-wrap max-h-48 overflow-y-auto font-mono text-[13px]">
                          {currentCallLog.discussion_notes}
                        </div>
                      </div>
                    ) : currentCallLog.status === 'completed' && (
                      <div className="flex flex-col items-center justify-center p-6 bg-white rounded-lg border border-dashed border-blue-200">
                        <FileText className="h-8 w-8 text-blue-200 mb-2" />
                        <p className="text-sm text-gray-500 mb-3">Transcript not fetched yet</p>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleFetchTranscript}
                          disabled={fetchingTranscript}
                          className="h-8"
                        >
                          {fetchingTranscript ? "Fetching..." : "Fetch Transcript"}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {viewingReviewNote.response.review_notes ? (
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-600 mb-2 block">
                      Review Notes
                    </Label>
                    <div className="bg-gray-50 p-4 rounded-lg border">
                      <div
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{
                          __html:
                            viewingReviewNote.response.review_notes ||
                            "No review notes available",
                        }}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {viewingReviewNote.response.reviewed_by && (
                      <div>
                        <Label className="text-sm font-medium text-gray-600">
                          Reviewed by
                        </Label>
                        <p className="text-sm bg-gray-50 p-2 rounded border">
                          {"Dr. " + (viewingReviewNote.response.reviewed_by_name || viewingReviewNote.response.reviewed_by)}
                        </p>
                      </div>
                    )}

                    {viewingReviewNote.response.reviewed_at && (
                      <div>
                        <Label className="text-sm font-medium text-gray-600">
                          Review Date
                        </Label>
                        <p className="text-sm bg-gray-50 p-2 rounded border">
                          {formatDate(viewingReviewNote.response.reviewed_at)}
                        </p>
                      </div>
                    )}

                    {viewingReviewNote.response.time_spent_on_review && (
                      <div>
                        <Label className="text-sm font-medium text-gray-600">
                          Review Time
                        </Label>
                        <p className="text-sm bg-gray-50 p-2 rounded border">
                          {formatTimeFromSeconds(
                            viewingReviewNote.response.time_spent_on_review,
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  {canEditreviewNote(viewingReviewNote.response) ? (
                    <>
                      <p className="text-gray-500 text-lg">
                        No review log available for this response
                      </p>
                      <div className="flex flex-wrap justify-center gap-3 mt-4">
                        {(isClinical || isDoctor || isBranchAdmin) && (
                          <>
                            {isLatestResponse(viewingReviewNote.response.id.toString()) && !currentCallLog && (
                              <Button
                                variant="outline"
                                onClick={() => navigate("/appointments", { state: { openAddModal: true, patientId: patient?.id } })}
                                className="flex items-center gap-2 border-orange-200 text-orange-700 hover:bg-orange-50"
                              >
                                <CalendarClock className="h-4 w-4" />
                                Schedule a Call
                              </Button>
                            )}
                            {isLatestResponse(viewingReviewNote.response.id.toString()) && isCallExpired() && (
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setCurrentCallLog(null);
                                  setIsScheduleCallModalOpen(true);
                                }}
                                className="flex items-center gap-2 border-red-200 text-red-600 hover:bg-red-50"
                              >
                                <CalendarClock className="h-4 w-4" />
                                Reschedule Call
                              </Button>
                            )}
                            {getCallNowStatus().canCall && (
                              <Button
                                variant="outline"
                                disabled={callNowLoading}
                                onClick={handleCallNow}
                                className="flex items-center gap-2 border-green-200 text-green-700 hover:bg-green-50"
                              >
                                <PhoneCall className="h-4 w-4" />
                                {callNowLoading ? "Calling..." : "Call Patient Now"}
                              </Button>
                            )}
                          </>
                        )}
                        <Button
                          onClick={() => {
                            setEditingResponse(viewingReviewNote.response);
                            setViewingReviewNote(null);
                          }}
                        >
                          Add Review Note
                        </Button>
                      </div>
                    </>
                  ) : (
                    <p className="text-gray-500 text-lg">
                      No review notes available
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end pt-4 border-t">
              <Button
                onClick={() => setViewingReviewNote(null)}
                variant="outline"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Review Note Editor Modal */}
      {editingResponse && canEditreviewNote(editingResponse) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center flex-wrap gap-2">
                Add Review Note for{" "}
                {formatTestName(editingResponse.questionnaire_type)} -{" "}
                {formatDate(editingResponse.created_at)}
                <Badge variant="default" className="text-xs bg-blue-100 text-blue-800 hover:bg-blue-100 border-blue-200">
                  Score: {editingResponse.total_score} {(() => {
                    try {
                      const qs = typeof editingResponse.answers === 'string'
                        ? JSON.parse(editingResponse.answers || "[]")
                        : (editingResponse.answers || []);
                      const mx = calculateMaxScore(qs);
                      return mx !== null ? `/ ${mx}` : "";
                    } catch (e) { return ""; }
                  })()}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  Most Recent Response
                </Badge>
              </h3>

              <div className="flex items-center space-x-3">
                <Label
                  htmlFor="modal-time-spent"
                  className="text-sm font-medium text-gray-600 whitespace-nowrap"
                >
                  Review time:
                </Label>
                <select
                  id="modal-time-spent"
                  value={selectedTimeSpent === null ? "" : selectedTimeSpent}
                  onChange={(e) =>
                    setSelectedTimeSpent(
                      e.target.value ? Number(e.target.value) : null,
                    )
                  }
                  className={`border rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${selectedTimeSpent === null
                    ? "border-red-300"
                    : "border-gray-300"
                    }`}
                >
                  <option value="">Select a time</option>
                  {timeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex-1 overflow-auto mb-4 space-y-6 pr-2">
              {/* Added detailed Q&A list for context while reviewing */}
              {(() => {
                try {
                  const questions = typeof editingResponse.answers === 'string'
                    ? JSON.parse(editingResponse.answers || "[]")
                    : (editingResponse.answers || []);

                  if (Array.isArray(questions) && questions.length > 0) {
                    return (
                      <div className="border-b pb-6">
                        <Label className="text-sm font-medium text-gray-600 mb-3 block">
                          Assessment Responses
                        </Label>
                        <div className="bg-gray-50/50 p-4 rounded-xl border border-gray-100 space-y-4">
                          {questions.map((q: any, i: number) => (
                            <div key={i} className="text-sm">
                              <div className="flex gap-2">
                                <span className="font-semibold text-blue-900 min-w-[24px]">Q{i + 1}:</span>
                                <span className="text-gray-800 font-medium">{q.question || 'Assessment Item'}</span>
                              </div>
                              <div className="ml-8 mt-1 space-y-1">
                                <div className="text-blue-700 font-medium italic">
                                  Result: {(q.selected_text || q.selected) ?? 'N/A'}
                                  {q.selected !== undefined && q.selected !== null ? ` (Score: ${q.selected})` : ''}
                                </div>
                                {q.comment && (
                                  <div className="text-xs text-gray-600 bg-white/50 p-2 rounded border border-gray-100 mt-1 italic">
                                    <span className="font-semibold not-italic">Note:</span> {q.comment}
                                  </div>
                                )}

                                {isClinical && q.voice_url && (
                                  <div className="mt-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => toggleAudio(q.voice_url)}
                                      className={`h-8 gap-2 bg-white ${playingAudio === q.voice_url ? 'border-blue-500 text-blue-700 shadow-inner' : 'text-gray-600 border-gray-200 shadow-sm'}`}
                                    >
                                      {playingAudio === q.voice_url ? (
                                        <>
                                          <PauseCircle className="w-4 h-4 animate-pulse" />
                                          Playing...
                                        </>
                                      ) : (
                                        <>
                                          <Mic className="w-3.5 h-3.5 text-blue-500" />
                                          <PlayCircle className="w-4 h-4" />
                                          Play Audio Reply
                                        </>
                                      )}
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return null;
                } catch (e) {
                  return null;
                }
              })()}

              <Label
                htmlFor="audit-note"
                className="text-sm font-medium mb-2 block"
              >
                Review Note (This will be saved to review_notes column)
              </Label>

              <SimpleRichTextEditor
                ref={auditEditorRef}
                value={reviewNote}
                onChange={setReviewNote}
                placeholder="Enter review notes for this questionnaire response..."
              />

              <div
                className={`mt-3 p-3 rounded-lg border ${selectedTimeSpent === null ? "bg-red-50 border-red-200" : "bg-blue-50 border-blue-200"}`}
              >
                <div className="text-sm">
                  <p
                    className={`font-medium mb-1 ${selectedTimeSpent === null ? "text-red-800" : "text-blue-800"}`}
                  >
                    {selectedTimeSpent === null
                      ? "⚠️ Please select review time before saving"
                      : `Selected Review Time: ${timeOptions.find((opt) => opt.value === selectedTimeSpent)?.label}`}
                  </p>
                  <p
                    className={`text-xs ${selectedTimeSpent === null ? "text-red-600" : "text-blue-600"}`}
                  >
                    This time will be saved with your review note
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => {
                  setEditingResponse(null);
                  setReviewNote("");
                  setSelectedTimeSpent(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={savereviewNote}
                className="bg-green-600 hover:bg-green-700"
                disabled={!reviewNote.trim()}
              >
                {selectedTimeSpent === null
                  ? "Save Review Note"
                  : `Save Review Note (${timeOptions.find((opt) => opt.value === selectedTimeSpent)?.label})`}
              </Button>
            </div>
          </div>
        </div>
      )}


      {/* CMS Alert History Card */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            CMS Alert History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {cmsAlertsLoading ? (
            <div className="flex items-center justify-center h-24">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#1a7a4a] border-t-transparent" />
            </div>
          ) : cmsAlerts.length === 0 ? (
            <div className="text-center py-8 text-gray-400 italic text-sm">
              No CMS alerts recorded for this patient.
            </div>
          ) : (
            <div className="space-y-3">
              {cmsAlerts.map((alert: any) => {
                const sevStyles: Record<string, string> = {
                  critical: "border-l-red-500 bg-red-50/30",
                  warning: "border-l-amber-500 bg-amber-50/20",
                  info: "border-l-blue-500 bg-blue-50/20",
                };
                const badgeStyles: Record<string, string> = {
                  critical: "bg-red-50 text-red-700 border-red-200",
                  warning: "bg-amber-50 text-amber-700 border-amber-200",
                  info: "bg-blue-50 text-blue-700 border-blue-200",
                };
                const statusStyles: Record<string, string> = {
                  active: "bg-orange-50 text-orange-700 border-orange-200",
                  acknowledged: "bg-purple-50 text-purple-700 border-purple-200",
                  resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
                  dismissed: "bg-gray-100 text-gray-500 border-gray-200",
                };
                const isLoading = cmsAlertActionLoading === alert.alert_id;
                return (
                  <div
                    key={alert.alert_id}
                    className={`flex items-start gap-4 p-4 rounded-xl border border-gray-100 border-l-4 ${sevStyles[alert.severity] || ""}`}
                  >
                    <div className="flex-shrink-0 pt-0.5">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border uppercase tracking-wider ${badgeStyles[alert.severity] || "bg-gray-100 text-gray-600 border-gray-200"}`}>
                        {alert.severity}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-gray-900">{alert.cms_measure_id}</span>
                        <span className="text-xs text-gray-400">{alert.questionnaire_type}</span>
                        {alert.is_trend_alert && (
                          <span className="text-xs text-purple-500 font-medium">Trend ×{alert.trend_consecutive_count}</span>
                        )}
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border capitalize ${statusStyles[alert.alert_status] || "bg-gray-100 text-gray-500 border-gray-200"}`}>
                          {alert.alert_status}
                        </span>
                      </div>
                      <div className="text-xs text-gray-600 mt-1">{alert.alert_message}</div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        Score {alert.computed_score} / {alert.threshold_value} · {formatDate(alert.alert_fired_at)}
                        {alert.cpt_billing_code && <span className="ml-2 font-medium text-[#1a7a4a]">CPT: {alert.cpt_billing_code}</span>}
                      </div>
                      {alert.resolution_notes && (
                        <div className="text-xs text-gray-500 mt-1 italic">Note: {alert.resolution_notes}</div>
                      )}
                    </div>
                    {alert.alert_status === "active" && (
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {isLoading ? (
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-[#1a7a4a] border-t-transparent" />
                        ) : (
                          <>
                            <button
                              onClick={() => handleCmsAcknowledge(alert.alert_id)}
                              title="Acknowledge"
                              className="p-1.5 rounded-lg border border-gray-200 hover:border-purple-300 hover:bg-purple-50 text-gray-400 hover:text-purple-600 transition-colors"
                            >
                              <Clock className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => { setCmsResolveModalId(alert.alert_id); setCmsResolveNotes(""); }}
                              title="Resolve"
                              className="p-1.5 rounded-lg border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50 text-gray-400 hover:text-emerald-600 transition-colors"
                            >
                              <CheckCircle className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => handleCmsDismiss(alert.alert_id)}
                              title="Dismiss"
                              className="p-1.5 rounded-lg border border-gray-200 hover:border-gray-400 hover:bg-gray-50 text-gray-400 hover:text-gray-600 transition-colors"
                            >
                              <XCircle className="h-3.5 w-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* CMS Resolve Modal */}
      {cmsResolveModalId !== null && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setCmsResolveModalId(null)}
        >
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-gray-900 mb-3">Resolve Alert</h3>
            <textarea
              className="w-full border border-gray-200 rounded-xl p-3 text-sm text-gray-700 resize-none focus:outline-none focus:border-[#1a7a4a]"
              rows={4}
              placeholder="Enter resolution notes (required)..."
              value={cmsResolveNotes}
              onChange={(e) => setCmsResolveNotes(e.target.value)}
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={() => setCmsResolveModalId(null)} className="rounded-xl">Cancel</Button>
              <Button size="sm" onClick={handleCmsResolve} className="rounded-xl bg-[#1a7a4a] hover:bg-[#155e39] text-white">Resolve</Button>
            </div>
          </div>
        </div>
      )}

      {/* Consent Information Card */}
      <Card>
        <CardHeader>
          <CardTitle>Consent Information & History</CardTitle>
        </CardHeader>
        <CardContent>
          {renderConsentHistory(patient.consent_logs)}
        </CardContent>
      </Card>

      {/* Call Logs & History Card */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-blue-500" />
            Call Logs & History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {allCallLogs.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Start Time</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">End Time</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Notes / Transcript</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {allCallLogs
                    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                    .map((log, index) => {
                      const callTimes = getCallTimes(log);
                      return (
                        <tr key={index} className="hover:bg-gray-50/50">
                          <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                            {formatDate(log.created_at)}
                          </td>
                          <td className="px-4 py-2 text-xs text-gray-800 font-mono whitespace-nowrap">
                            {callTimes.startTime}
                          </td>
                          <td className="px-4 py-2 text-xs text-gray-800 font-mono whitespace-nowrap">
                            {callTimes.isInProgress ? (
                              <span className="inline-flex items-center gap-1 text-amber-600 font-medium font-sans">
                                <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                                In Progress
                              </span>
                            ) : (
                              callTimes.endTime
                            )}
                          </td>
                          <td className="px-4 py-2 text-xs text-gray-700 font-mono whitespace-nowrap">
                            {callTimes.isInProgress ? (
                              <span className="text-amber-600 font-medium font-sans">Live</span>
                            ) : (
                              callTimes.duration
                            )}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900 font-medium capitalize whitespace-nowrap">
                            {log.call_type}
                          </td>
                          <td className="px-4 py-2 text-sm whitespace-nowrap">
                            <Badge
                              variant="outline"
                              className={
                                log.status === 'completed'
                                  ? 'bg-green-50 text-green-700 border-green-200'
                                  : log.status === 'in_progress'
                                  ? 'bg-amber-50 text-amber-800 border-amber-300'
                                  : 'bg-orange-50 text-orange-700 border-orange-200'
                              }
                              title={log.status === 'in_progress' ? 'Amazon Connect outbound call initiated and currently ongoing' : undefined}
                            >
                              {log.status === 'in_progress' && (
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-ping mr-1" />
                              )}
                              {log.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-600 max-w-xs">
                            <div className="truncate italic">
                              {log.discussion_notes || log.notes || "No notes available"}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 italic">
              No call history recorded for this patient.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Schedule Call Modal */}
      <Dialog open={isScheduleCallModalOpen} onOpenChange={setIsScheduleCallModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-orange-600" />
              Schedule a Call to Patient
            </DialogTitle>
            <DialogDescription>
              Set a date and time to call {patient?.first_name}{patient?.middle_name ? ` ${patient.middle_name}` : ''} {patient?.last_name}.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="date" className="text-right">
                Date
              </Label>
              <Input
                id="date"
                type="date"
                className="col-span-3"
                value={scheduleCallDate}
                min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                onChange={(e) => setScheduleCallDate(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="time" className="text-right">
                Time
              </Label>
              <Input
                id="time"
                type="time"
                className="col-span-3"
                value={scheduleCallTime}
                onChange={(e) => setScheduleCallTime(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsScheduleCallModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleScheduleCall}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              Schedule Call
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MindWiseSyncDialog
        open={isMindWiseModalOpen}
        onOpenChange={setIsMindWiseModalOpen}
        syncResult={mindwiseSyncResult}
        patientId={id || "demo-pt-1"}
        patientName={patient ? `${patient.first_name || ""} ${patient.last_name || ""}`.trim() : "Patient"}
        onReSync={handleMindWiseSync}
        isSyncing={mindwiseSyncing}
      />
    </div>
  );
}

export default function PatientDetails() {
  return (
    <MainLayout>
      <PatientDetailsContent />
    </MainLayout>
  );
}
